/* eslint-disable max-statements */
import { Injectable, Inject } from '@angular/core';
import { core, logger } from '@icc/shared/helpers';
import { LoadedConfiguratorsDataValue } from '@icc/shared/configurators/configurators-data.service';
import { WindowActiveConfiguration, Filling } from '@icc/window-door/types';
import { DoorActiveConfiguration } from '@icc/window-door/types';
import { Common } from '@icc/shared/helpers';
import { GlazingBeadsService } from '../../glazing-beads.service';
import {
    IssueLevel,
    InfoService,
    isArray,
    isObject
} from '@icc/shared/helpers';
import { StepsService } from '@icc/configurator/shared';
import { NewColorsService, ProfilesService, ValidationService } from '@icc/construction-elements/configurator';
import { BondedGlazingService } from './bonded-glazing.service';
import { ColorMappingService } from '@icc/construction-elements/configurator';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared/config';
import { GlazingSizesService } from './glazing-sizes.service';
import { ConfigurationsService } from '@icc/configurator/shared';
import { FillingsListPageComponent } from '../../fillings-list-page/fillings-list-page.component';
import { DecoPanelOptionsPageComponent } from '../../deco-panel-options-page/deco-panel-options-page.component';
import { PanelGlazingsListPageComponent } from '../../panel-glazings-list-page/panel-glazings-list-page.component';
import { PanelTrimsListPageComponent } from '../../panel-trims-list-page/panel-trims-list-page.component';
import { IccFilling, IccGlassType, IccGlassTypeVariant } from '@icc/window-door/types';
import { BehaviorSubject } from 'rxjs';
import { GlazingUnitsService } from './glazing-units.service';
import { GlazingUnitElement } from '@icc/window-door/types';
import { ActiveSash } from '@icc/window-door/types';
import { ModelListPageComponent } from '../../door/model-list-page/model-list-page.component';
import { ColorRestrictionService } from '@icc/construction-elements/configurator';
import type { IccDoorPanelTrim } from '@icc/window-door/types';
import { ModalService } from '@icc/configurator/shared';
import { EventBusService, ConfiguratorsDataService, TranslateService, BaseIssuesService } from "@icc/shared";
import { UserService } from "@icc/shared/core";

@Injectable()
export class FillingsService {
    fillings: IccFilling[] = [];
    fillingsCategories = [];
    glassTypes: IccGlassType[] = [];
    glassTypeVariants: IccGlassTypeVariant[] = [];
    doorPanelTrims: IccDoorPanelTrim[] = [];
    modalGlassTypeData = {
        glassTab: 'glazing',
        selectedSecurity: null,
        selectedOrnament: null,
        selectedUg: null,
        selectedRw: null,
        selectedCategory: null,
    };
    hasGlasses = false;
    private loadedData = false;
    private allFillings: IccFilling[] = [];
    private decoPanelRestrictiveDimensions = {
        minWidth: 0,
        maxWidth: Number.POSITIVE_INFINITY,
        minHeight: 0,
        maxHeight: Number.POSITIVE_INFINITY,
    };

    constructor(
        private userService: UserService,
        private eventBusService: EventBusService,
        private infoFactory: InfoService,
        private modalService: ModalService,
        private glazingBeadsService: GlazingBeadsService,
        private issuesService: BaseIssuesService,
        private validationService: ValidationService,
        private bondedGlazingService: BondedGlazingService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private translateService: TranslateService,
        private glazingSizesService: GlazingSizesService,
        private glazingUnitsService: GlazingUnitsService,
        private configurationsService: ConfigurationsService<"window">,
        private configuratorsDataService: ConfiguratorsDataService,
        private profilesService: ProfilesService,
        private stepsService: StepsService,
        private newColorsService: NewColorsService
    ) {
        this.eventBusService.subscribe<LoadedConfiguratorsDataValue>(
            'loadedConfiguratorsData',
            data => {
                this.loadFillings(data.value);
                this.decoPanelRestrictiveDimensions = data.value.panelsSettings;
                this.loadedData = true;
                this.hasGlasses = !!(
                    data.value.glasses
                    && data.value.glasses.length
                    && data.value.interPaneSpaces
                    && data.value.interPaneSpaces.length
                );
                this.doorPanelTrims = data.value.doorPanelTrims || [];
                if (data.activeConfiguration) {
                    this.loadMatchingFillings(
                        data.activeConfiguration as WindowActiveConfiguration
                    );
                }
            }
        );

        this.eventBusService.subscribe('*', data =>
            this.glazingSizesService.count(data.activeConfiguration)
        );

        this.eventBusService.subscribe('setSystem', data => {
            try {
                this.loadMatchingFillings(data.activeConfiguration as WindowActiveConfiguration);
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe('setConstructionColor', data => {
            try {
                if (
                    WindowActiveConfiguration.is(data.activeConfiguration)
                    && (data.activeConfiguration.type !== 'door'
                        || !data.activeConfiguration.System.door_type)
                ) {
                    this.loadMatchingFillings(data.activeConfiguration);
                }
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe('loadedGlazingBeads', data => {
            try {
                this.loadMatchingFillings(data.activeConfiguration as WindowActiveConfiguration);
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe('loadedFillings', data => {
            try {
                this.validateFillingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
                this.validatePanelGlazingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration
                );
                if(data.activeConfiguration.type === 'door') {
                    this.updateStepLights(data.activeConfiguration as WindowActiveConfiguration);
                }
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe('changedSashes', data => {
            try {
                this.validateFillingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration,
                    true
                );
                this.updateStepLights(data.activeConfiguration as WindowActiveConfiguration);
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe('changedFillings', data => {
            try {
                this.validateFillingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration,
                    data.value ? false : true
                );
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe<any>('setFrameProfile', data => {
            try {
                this.validateFillingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
                this.updateStepLights(data.activeConfiguration as WindowActiveConfiguration);
            } catch (err) {
                logger.error(err);
            }
        });
        this.eventBusService.subscribe<any>('setSashProfile', data => {
            try {
                this.validateFillingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
                this.updateStepLights(data.activeConfiguration as WindowActiveConfiguration);
            } catch (err) {
                logger.error(err);
            }
        });
        this.eventBusService.subscribe<any>('setMullionProfile', data => {
            try {
                this.validateFillingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
                this.updateStepLights(data.activeConfiguration as WindowActiveConfiguration);
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe<any>('validatedProfiles', data => {
            try {
                this.validateFillingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
                this.validatePanelGlazingsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration
                );
                this.validatePanelTrimsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration
                );
                if(data.activeConfiguration.type === 'door') {
                    this.updateStepLights(data.activeConfiguration as WindowActiveConfiguration);
                    // this.validateFillingColors(data.activeConfiguration as WindowActiveConfiguration, 'activeDoorSash');
                }
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe('setGlazingInSash', data => {
            try {
                this.checkSingleFilling(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
                this.validateFillings(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe(['setPanelGlazing', 'setPanelTrim'], data => {
            try {
                this.checkSingleFilling(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
            } catch (err) {
                logger.error(err);
            }
        });

        this.eventBusService.subscribe('setFillingColor', data => {
            try {
                this.checkSingleFillingColors(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.defaultConfiguration as WindowActiveConfiguration
                );
            } catch (err) {
                logger.error(err);
            }
        });
    }

    setFillingInAllSashes(conf: WindowActiveConfiguration, newFilling, isDefault = false) {
        const pauseId = this.eventBusService.pause(['setFillingColor', 'setGlazingInSash']);
        conf.Sashes.forEach(sash => {
            this.setFillingInSash(sash, sash, newFilling, conf, isDefault);
            sash.intSashes.forEach(field => {
                this.setFillingInSash(field, sash, newFilling, conf, isDefault);
            });
        });
        this.eventBusService.resume(['setFillingColor', 'setGlazingInSash'], pauseId);
    }

    setFillingInFilteredSashes(
        conf: WindowActiveConfiguration,
        newFilling,
        isDefault = false,
        filter: (sash) => boolean = () => true,
        defaultConf?,
        door?: 'doorActive' | 'doorActiveInner' | 'doorPassive',
        changeLightsGlazing = false
    ) {
        const setGlazingModel = newFilling.type === 'deco_panel_glazing';
        const pauseId = this.eventBusService.pause(['setFillingColor', 'setGlazingInSash']);
        conf.Sashes.filter(filter).forEach(sash => {
            if (setGlazingModel && defaultConf) {
                newFilling = this.getDefaultFillingInSash(sash, sash, conf, defaultConf);
            }
            if(!changeLightsGlazing || (changeLightsGlazing && !sash.glazing.glazing_to_filter)) {
                this.setFillingInSash(sash, sash, newFilling, conf, isDefault, door);
            }
            sash.intSashes.forEach(field => {
                if (setGlazingModel && defaultConf) {
                    newFilling = this.getDefaultFillingInSash(sash, sash, conf, defaultConf);
                }
                this.setFillingInSash(field, sash, newFilling, conf, isDefault);
            });
        });
        this.eventBusService.resume(['setFillingColor', 'setGlazingInSash'], pauseId);
    }

    setFillingInSash(field, sash, newFilling, conf, isDefault = false, door?: 'doorActive' | 'doorActiveInner' | 'doorPassive') {
        if (!newFilling || !newFilling.id) {
            return;
        }
        if(newFilling?.selected || !field.defaultGlazing) {
            field.defaultGlazing = newFilling;
        }
        newFilling = core.copy(newFilling);
        newFilling.isDefault = isDefault;
        field.glazing = newFilling;

        if (
            field.id === sash.id
            && field.intSashes.length === 1
            && field.intSashes[0].glazing.id !== newFilling.id
        ) {
            this.setFillingInSash(field.intSashes[0], sash, newFilling, conf, isDefault);
        }
        if (field.id !== sash.id && sash.glazing.id !== newFilling.id) {
            this.setFillingInSash(sash, sash, newFilling, conf, isDefault);
        }
        this.setDecoPanelDefaults(field, sash, conf, door);
        this.eventBusService.post({
            key: 'setGlazingInSash',
            value: {
                glazingId: newFilling.id,
                sashId: sash,
            },
            conf,
        });
    }

    setDefaultFillingInSash(field, sash, conf: WindowActiveConfiguration, defaultConf, showInfoForUser = false) {
        const defaultFilling = this.getDefaultFillingInSash(field, sash, conf, defaultConf, showInfoForUser);
        let isDefault = true;

        if(defaultConf.OneGlazing && defaultConf.Glazing.id && !defaultConf.Glazing.isDefault && String(defaultFilling.id) === String(defaultConf.Glazing.id)) {
            isDefault = false;
        }
        this.setFillingInSash(field, sash, defaultFilling, conf, isDefault);
        this.eventBusService.post({
            key: 'changedFillings',
            value: {},
        });
    }

    getDefaultFillingInSash(field, sash, conf: WindowActiveConfiguration, defaultConf, showInfoForUser = false) {
        const matchingFillings = this.getMatchingFillingsInSash(field, sash, conf);
        const sameGlazingDifferentThickness =
            sash.glazing
            && sash.glazing.id
            && matchingFillings.find(
                o => Number(o.id.split('.')[0]) === Number(sash.glazing.id.split('.')[0])
            );
        const defaultGlazingForSystem = matchingFillings.find(
            o => {
                if (conf.System.default_market_configuration?.glass_type_id) {
                    return Number(o.id.split('.')[0]) === Number(conf.System.default_market_configuration.glass_type_id);
                } else {
                    return Number(o.id.split('.')[0]) === Number(conf.System.default_glass_type_id);
                }
            }
        );

        const frame = conf.Frames.find(f => f.id === sash.frameId);
        const notDoorSash = (s) => ['DRA', 'DRP', 'DOA', 'DOP'].indexOf(s.type.type) === -1;
        const isDoorLight = (s) => conf.type === 'door' && notDoorSash(s);
        const isSideLight = (s) => isDoorLight(s) && frame.y + s.ry + s.rHeight === conf.Height;
        const isTopLight = (s) => isDoorLight(s) && frame.y + s.ry + s.rHeight < conf.Height;
        let configSashFillings;
        if(isSideLight(sash)) {
            configSashFillings = frame.x === 0 ? conf.OneFilling.doorLeftLight : conf.OneFilling.doorRightLight;
            const activeSash = conf.Sashes.find(s => ['DRA', 'DOA'].indexOf(s.type.type) !== -1);
            if(activeSash?.glazing?.glazing_ids?.length > 0 && configSashFillings) {
                configSashFillings = activeSash.glazing.default_glazing_id ?? activeSash.glazing.glazing_ids[0];
                configSashFillings = matchingFillings.find(f => Number(f.id.split('.')[0]) === Number(configSashFillings))?.id;
            } else {
                const currentSideLight = matchingFillings.find(f => f.id === configSashFillings);
                if(configSashFillings && currentSideLight?.glazing_to_filter) {
                    configSashFillings = matchingFillings.filter(f => !f.glazing_to_filter)[0].id;
                }
            }
        } else if(isTopLight(sash)){
            configSashFillings = conf.OneFilling.doorTopLight;
        }
        if(configSashFillings) {
            configSashFillings = matchingFillings.find(f => f.id === configSashFillings);
        }

        const defaultDecoPanel = matchingFillings.find(
            o => DoorActiveConfiguration.is(conf) && conf.Model && o.id === conf.Model.id
        );
        const defaultDecoPanelPassive = matchingFillings.find(
            o =>
                DoorActiveConfiguration.is(conf)
                && conf.ModelOptions.passiveSashModel
                && Number(o.id.split('.')[0])
                    === Number(conf.ModelOptions.passiveSashModel.id.split('.')[0])
        );
        let defaultGlazing;
        let filling;
        if(this.config().IccConfig.Configurators.window.fillingsMapping) {
            const thermalTransmittanceFilling = this.getFillingBaseOnMappingThermalTransmittance(field, sash, conf);
            const thicknessFilling = this.getFillingBaseOnMappingThickness(field, sash, conf, thermalTransmittanceFilling);
            if(thicknessFilling) {
                if(!(!field.glazing || !field.glazing.id || thicknessFilling.id === sash.glazing.id) && showInfoForUser) {
                    this.infoFactory.openWarning(this.config().preset === 'b2b'
                        ? this.translateService.instant('WINDOW|Przekroczono wymiary szklenia. Szklenie ustawione zgodnie z mapowaniem')
                        : this.translateService.instant('WINDOW|Szklenie ustawione zgodnie z mapowaniem')
                    );
                }
                return thicknessFilling;
            }
            if(conf.OneFilling.defaultWindow) {
                filling = core.fIdO(matchingFillings, conf.OneFilling.defaultWindow);
            }
        }
        if (configSashFillings) {
            filling = configSashFillings;
        }
        if (!filling && defaultDecoPanelPassive && ['DRP', 'DOP'].indexOf(sash.type.type) > -1) {
            filling = core.copy(defaultDecoPanelPassive);
        }
        if (
            !filling
            && defaultDecoPanel
            && ['DRA', 'DRP', 'DOA', 'DOP'].indexOf(sash.type.type) > -1
        ) {
            filling = core.copy(defaultDecoPanel);
        }

        if (!filling && sash.type.type === 'F' && this.isDoorPanel(field.glazing)) {
            filling = core.copy(defaultGlazingForSystem);
        }
        if(!filling && defaultConf.OneGlazing && defaultConf.Glazing && defaultConf.Glazing.id && defaultConf.Glazing.isDefault) {
            filling = core.copy(defaultGlazingForSystem);
        }
        if (!filling && sameGlazingDifferentThickness) {
            filling = core.copy(sameGlazingDifferentThickness);
        }

        if (!filling && defaultConf.OneGlazing && defaultConf.Glazing && defaultConf.Glazing.id) {
            defaultGlazing = core.fIdO(matchingFillings, defaultConf.Glazing.id);
        }
        if (!filling && !defaultGlazing && defaultConf.OneGlazing && defaultConf.OneFilling && (defaultConf.OneFilling.window || defaultConf.OneFilling.doorActive)) {
            defaultGlazing = core.fIdO(matchingFillings, defaultConf.OneFilling.window || defaultConf.OneFilling.doorActive);
        }
        if (!filling && defaultGlazing) {
            filling = core.copy(defaultGlazing);
        }
        if (!filling && defaultGlazingForSystem) {
            filling = core.copy(defaultGlazingForSystem);
        }
        if (!filling) {
            filling = core.copy(matchingFillings[0]);
        }

        if (
            filling
            && field.id !== sash.id
            && sash.glazing.id === filling.id
            && sash.glazing.selectedColor
        ) {
            filling.selectedColor = core.copy(sash.glazing.selectedColor);
        }

        return filling;
    }

    setDecoPanelDefaults(field, sash, conf, doorSide?: 'doorActive' | 'doorActiveInner' | 'doorPassiveInner' | 'doorPassive') {
        const pausedEvents = ['processDependencies', 'setConstructionColor', 'setProfileColor', 'setAccessoryColor', 'setLippingColor', 'setThresholdColor', 'setDoorPortalColor', 'setMuntinColor', 'changedDoorHardware'];
        const pauseId = this.eventBusService.pause(pausedEvents);
        if (!this.isDoorPanel(sash.glazing)) {
            sash.panelGlazing = {};
            sash.panelTrim = {};
            sash.innerPanelTrim = {};
            sash.panelInner = null;
            sash.intSashes?.forEach(intSash => {
                intSash.panelGlazing = {};
                intSash.panelTrim = {};
                intSash.innerPanelTrim = {};
                intSash.panelInner = null;
            });
        } else {
            if (field.glazing.type === 'door_panels' || !this.isValidPanelType(sash)) {
                this.setDefaultPanelTypeInSash(sash);
            }
            if (!this.isValidPanelGlazing(sash)) {
                this.setDefaultPanelGlazingInSash(sash);
            }
            if (!this.isValidPanelTrim(sash, 'outside')) {
                this.setDefaultPanelTrimInSash(sash, conf, 'outside');
            }
            if (!this.isValidInnerPanel(sash, conf, doorSide)) {
                this.setDefaultInnerPanelInSash(sash, sash, conf, doorSide);
            }
            sash.intSashes.forEach(intSash => {
                if (!this.isValidInnerPanel(intSash, conf, doorSide)) {
                    this.setDefaultInnerPanelInSash(intSash, sash, conf, doorSide);
                }
            });
            if (!this.isValidPanelTrim(sash, 'inside')) {
                this.setDefaultPanelTrimInSash(sash, conf, 'inside');
            }
        }
        if (sash.glazing.type !== 'glazing' && sash.glazing.type !== 'panels_glazing') {
            this.validateFillingColors(conf, sash);
        }
        this.eventBusService.resume(pausedEvents, pauseId);
    }

    setDefaultModelOptions(conf: DoorActiveConfiguration) {
        if (conf.Model) {
            const matchingPanelTypes = this.getMatchingPanelTypesForModel(conf);
            conf.ModelOptions.panelType = this.getMatchingPanelType(matchingPanelTypes);
            if (!this.isValidPanelGlazingForModel(conf)) {
                conf.ModelOptions.panelGlazing = core.copy(this.getMatchingPanelGlazingsForModel(conf)[0]);
                conf.ModelOptions.passivePanelGlazing = core.copy(this.getMatchingPanelGlazingsForDoorSashes(conf, false)[0]);
            }
            if (!this.isValidInnerPanelForModel(conf)) {
                if (conf.System?.door_type) {
                    const panels = this.getPanelsBasedOnPanelBaseSide(conf);
                    conf.ModelOptions.panelInner = core.copy(panels[0]);
                } else {
                    conf.ModelOptions.panelInner = core.copy(this.getMatchingInnerPanelsForModel(conf)[0]);
                }
            }
        }
    }

    clearPredefinedLayoutMeta(conf: DoorActiveConfiguration) {
        if (!conf || !conf.ModelOptions) {
            return;
        }
        const opts = conf.ModelOptions as any;
        const hadPredefinedLayout = Boolean(
            opts.predefinedLayoutId
            || opts.predefinedLayoutName
            || opts.predefinedLayoutImage
        );
        opts.predefinedLayoutId = null;
        opts.predefinedLayoutName = '';
        opts.predefinedLayoutImage = '';
        if (hadPredefinedLayout) {
            opts.hasBeenChosen = false;
            conf.Model = null;
        }
    }

    setDefaultModelOptionsPassive(conf: DoorActiveConfiguration) {
        if (conf.ModelOptions) {
            if (!this.isValidPassiveInnerPanelForModel(conf)) {
                if (conf.System?.door_type) {
                    const panels = this.getPassiveInnerPanelsBasedOnPanelBaseSide(conf);
                    conf.ModelOptions.passiveInnerSashModel = core.copy(panels[0]);
                } else {
                    conf.ModelOptions.passiveInnerSashModel = core.copy(this.getMatchingPassiveInnerPanelsForModel(conf)[0]);
                }
            }
        }
    }

    setPanelGlazingInSash(sash, panelGlazing) {
        sash.panelGlazing = core.copy(panelGlazing);
        this.eventBusService.post({
            key: 'setPanelGlazing',
            value: null,
        });
    }

    setPanelTrimInSash(sash, panelTrim, conf, face: 'inside' | 'outside' = 'outside', isDefault = false) {
        if (face === 'inside') {
            sash.innerPanelTrim = core.copy(panelTrim);
            if (sash.innerPanelTrim) {
                sash.innerPanelTrim.isDefault = isDefault;
            }
        } else {
            sash.panelTrim = core.copy(panelTrim);
            if (sash.panelTrim) {
                sash.panelTrim.isDefault = isDefault;
            }

            if (!this.isValidPanelTrim(sash, 'inside')) {
                this.setDefaultPanelTrimInSash(sash, conf, 'inside');
            }
        }
        this.eventBusService.post({
            key: 'setPanelTrim',
            value: null,
        });
    }

    setInnerPanelInFilteredSashes(
        conf: DoorActiveConfiguration,
        newFilling,
        isDefault = false,
        filter: (sash) => boolean = () => true,
        defaultConf?,
        doorSide?: 'doorActiveInner' | 'doorActive' | 'doorPassiveInner'
    ) {
        conf.Sashes.filter(filter).forEach(sash => {
            this.setInnerPanelInSash(sash, sash, newFilling, conf, isDefault, doorSide);
            sash.intSashes.forEach(field => {
                this.setInnerPanelInSash(field, sash, newFilling, conf, isDefault);
            });
        });
    }

    // eslint-disable-next-line max-params
    setInnerPanelInSash(
        field,
        sash: ActiveSash,
        newFilling,
        conf: DoorActiveConfiguration,
        isDefault = false,
        doorSide?: 'doorActiveInner' | 'doorActive' | 'doorPassiveInner'
    ) {
        if (!newFilling || !newFilling.id) {
            return;
        }
        newFilling = core.copy(newFilling);
        newFilling.isDefault = isDefault;
        newFilling.selectedColor = field.panelInner?.selectedColor;
        field.panelInner = newFilling;
        if (
            field.id === sash.id
            && field.intSashes.length === 1
            && (!field.intSashes[0].panelInner
                || field.intSashes[0].panelInner.id !== newFilling.id)
        ) {
            this.setInnerPanelInSash(field.intSashes[0], sash, newFilling, conf, isDefault, doorSide);
        }
        if (field.id !== sash.id && (!sash.panelInner || sash.panelInner.id !== newFilling.id)) {
            this.setInnerPanelInSash(sash, sash, newFilling, conf, isDefault);
        }

        this.setDefaultPanelTypeInSash(sash);
        if (!this.isValidPanelTrim(sash, 'inside')) {
            this.setDefaultPanelTrimInSash(sash, conf, 'inside');
        }
        this.setDecoPanelDefaults(field, sash, conf, doorSide);

        this.eventBusService.post({
            key: 'setGlazingInSash',
            value: {
                glazingId: newFilling.id,
                sashId: sash,
            },
            conf,
        });
    }

    setDefaultPanelGlazingInSash(sash) {
        const defaultPanelGlazing = this.getDefaultPanelGlazing(sash);
        this.setPanelGlazingInSash(sash, defaultPanelGlazing);
    }
    setDefaultPanelTrimInSash(sash, conf, face: 'inside' | 'outside' = 'outside') {
        const defaultPanelTrim = this.getDefaultPanelTrim(sash, conf, face);
        this.setPanelTrimInSash(sash, defaultPanelTrim, conf, face, true);
    }

    setDefaultInnerPanelInSash(field, sash, conf: DoorActiveConfiguration, doorSide: 'doorActive' | 'doorActiveInner' | 'doorPassiveInner' | 'doorPassive') {
        let matchingFillings = this.getMatchingInnerPanelsForSash(field, sash, conf);
        if (matchingFillings.length === 0) {
            field.panelInner = null;
            sash.panelInner = null;
            return;
        }

        if (conf.System.door_type && (doorSide === 'doorActive' ||  doorSide === 'doorPassive')) {
            const outerFillingId = sash?.glazing?.id;
            if (sash?.glazing?.base_side === 'inside') {
                matchingFillings = matchingFillings.filter(
                    (p) => p.base_side === 'inside' && Number(p.id) === Number(outerFillingId)
                );
            } else {
                matchingFillings = matchingFillings.filter(
                    (p) =>
                        (p.base_side === 'outside' && Number(p.id) === Number(outerFillingId)) ||
                        p.base_side === 'any'
                );
            }
        }

        const defaultPanelForSystem = matchingFillings.find(
            o => {
                if (conf.System.default_market_configuration?.glass_type_id) {
                    return Number(o.id.split('.')[0]) === Number(conf.System.default_market_configuration.glass_type_id);
                } else {
                    return Number(o.id.split('.')[0]) === Number(conf.System.default_glass_type_id);
                }
            }
        );


        let filling;
        if (!filling && defaultPanelForSystem && doorSide !== 'doorActiveInner') {
            filling = core.copy(defaultPanelForSystem);
        }
        if (!filling) {
            filling = core.copy(matchingFillings[0]);
        }

        if (
            filling
            && field.id !== sash.id
            && sash.panelInner.id === filling.id
            && sash.panelInner.selectedColor
        ) {
            filling.selectedColor = core.copy(sash.panelInner.selectedColor);
        }
        this.setInnerPanelInSash(field, sash, filling, conf, true);
    }

    setPanelTypeInSash(sash, panelType) {
        sash.panelType = panelType;
        if (sash.intSashes) {
            sash.intSashes.forEach(field => {
                this.setPanelTypeInSash(field, panelType);
            });
        }
    }

    getPanelsBasedOnPanelBaseSide(conf, noValidation = false) {
        const sash = Number(
            conf.Sashes.find((s) => ['DRA', 'DOA'].includes(s.type.type))?.glazing?.id
        );
        return this.getMatchingInnerPanelsForModel(conf, noValidation).filter(
            (p) =>
                (p.base_side === 'outside' && Number(p.id) === sash) ||
                p.base_side === 'any' ||
                p.base_side === 'inside'
        );
    }

    getPassiveInnerPanelsBasedOnPanelBaseSide(conf, noValidation = false) {
        const sash = Number(
            conf.Sashes.find((s) => ['DRP', 'DOP'].includes(s.type.type))?.glazing?.id
        );
        return this.getMatchingPassiveInnerPanelsForModel(conf, noValidation).filter(
            (p) =>
                (p.base_side === 'outside' && Number(p.id) === sash) ||
                p.base_side === 'any' ||
                p.base_side === 'inside'
        );
    }

    setDefaultPanelTypeInSash(sash) {
        const matchingPanelTypesForSash = this.getMatchingPanelTypesForSash(sash);
        const defaultPanelType = this.getMatchingPanelTypeForSash(sash, matchingPanelTypesForSash);
        this.setPanelTypeInSash(sash, defaultPanelType);
    }

    getDefaultPanelGlazing(sash) {
        const matchingPanelGlazings = this.getMatchingPanelGlazingsForSash(sash);
        return matchingPanelGlazings[0];
    }

    getDefaultPanelGlazingForModel(conf: DoorActiveConfiguration) {
        const matchingPanelGlazings = this.getMatchingPanelGlazingsForModel(conf);
        return matchingPanelGlazings[0];
    }

    getDefaultPanelInner(field, sash, conf: DoorActiveConfiguration) {
        const matchingInnerPanels = this.getMatchingInnerPanelsForSash(field, sash, conf);
        return matchingInnerPanels[0];
    }

    getDefaultPanelTrim(sash, conf, face: 'inside' | 'outside' = 'outside') {
        const matchingPanelTrims = this.getMatchingPanelTrimsForSash(sash, conf, face);
        if (face === 'inside' && sash.panelTrim?.id && matchingPanelTrims.some(p => +p.id === +sash.panelTrim.id)) {
            return core.copy(sash.panelTrim);
        }
        return matchingPanelTrims[0];
    }

    getDefaultPanelTrimForModel(conf: DoorActiveConfiguration, face: 'inside' | 'outside' = 'outside') {
        const matchingPanelTrims = this.getMatchingPanelTrimsForModel(conf, face);
        if (face === 'inside' && conf.ModelOptions.panelTrim?.id && matchingPanelTrims.some(p => +p.id === +conf.ModelOptions.panelTrim.id)) {
            return core.copy(conf.ModelOptions.panelTrim);
        }
        return matchingPanelTrims[0];
    }

    setPanelOptionsInAllSashes(conf: WindowActiveConfiguration, newGlazing, panelType) {
        const pauseId = this.eventBusService.pause(['setPanelGlazing']);
        conf.Sashes.forEach(sash => {
            this.setPanelGlazingInSash(sash, newGlazing);
            this.setPanelTypeInSash(sash, panelType);
            sash.intSashes.forEach(field => {
                this.setPanelGlazingInSash(field, newGlazing);
                this.setPanelTypeInSash(field, panelType);
            });
        });
        this.eventBusService.resume(['setPanelGlazing'], pauseId);
    }

    setPanelGlazingsInAllSashes(conf: WindowActiveConfiguration, newGlazing) {
        const pauseId = this.eventBusService.pause(['setPanelGlazing']);
        conf.Sashes.forEach(sash => {
            this.setPanelGlazingInSash(sash, newGlazing);
            sash.intSashes.forEach(field => {
                this.setPanelGlazingInSash(field, newGlazing);
            });
        });
        this.eventBusService.resume(['setPanelGlazing'], pauseId);
    }

    setPanelTrimsInAllSashes(conf: WindowActiveConfiguration, newTrim, face: 'inside' | 'outside' = 'outside') {
        const pauseId = this.eventBusService.pause(['setPanelTrim']);
        conf.Sashes.forEach(sash => {
            this.setPanelTrimInSash(sash, newTrim, conf, face);
            sash.intSashes.forEach(field => {
                this.setPanelTrimInSash(field, newTrim, conf, face);
            });
        });
        this.eventBusService.resume(['setPanelTrim'], pauseId);
    }

    setDecorativePanelInDoorSashes(
        conf: DoorActiveConfiguration,
        defaultConf: any,
        filling: IccFilling,
        field = 'door',
        changeLightsGlazing = false
    ) {
        if (DoorActiveConfiguration.is(conf) && (field === 'door' || field === 'doorActive')) {
            this.clearPredefinedLayoutMeta(conf);
            conf.Model = filling;
            conf.ModelOptions.hasBeenChosen = true;
            this.setDefaultModelOptions(conf);
        }
        if (DoorActiveConfiguration.is(conf) && field === 'doorPassive') {
            this.clearPredefinedLayoutMeta(conf);
            conf.ModelOptions.passiveSashModel = filling;
            this.setDefaultModelOptionsPassive(conf);
        }
        if (DoorActiveConfiguration.is(conf) && field === 'doorPassiveInner') {
            conf.ModelOptions.passiveInnerSashModel = filling;
        }
        if (field !== 'doorActiveInner' && field !== 'doorPassiveInner') {
            this.setFillingInFilteredSashes(
                conf,
                filling,
                false,
                this.getFilterForField(field, conf),
                defaultConf,
                field === 'doorPassive' ? 'doorPassive' : 'doorActive',
                changeLightsGlazing
            );
        } else {
            this.setInnerPanelInFilteredSashes(
                conf,
                filling,
                false,
                this.getFilterForField(field, conf),
                defaultConf,
                field
            );
        }

        if (this.config().IccConfig.Configurators.dependencies) {
            this.eventBusService.post({ key: 'processDependencies', value: null });
        }
        this.eventBusService.post({
            key: 'changedFillings',
            value: field === 'doorLight',
        });
        if (field !== 'doorLight') {
            const fillingsData = this.getPanelGlazingsByFieldType(field, conf);
            const sash = fillingsData.sash;
            if (field === 'doorActiveInner') {
                this.setDecoPanelDefaults(sash, sash, conf, field);
            } else {
                this.setDecoPanelDefaults(sash, sash, conf);
            }
        }
        return Promise.resolve()
            .then(() => {
                if (field !== 'doorActiveInner' && field !== 'doorLight' && field !== 'doorPassiveInner') {
                    if (filling.type === 'door_panels') {
                        return this.changePanelGlazing((field === 'door' || field === 'doorActive') ? 'doorActive' : (field === 'doorPassive' ? 'doorPassive' : 'door'), conf);
                    } else {
                        return this.openModalPanelOptions(field === 'door' ? 'doorActive' : 'door', conf);
                    }
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                if (field !== 'doorLight' && filling.type === 'door_panels') {
                    return this.changePanelTrim(field === 'door' ? 'doorActive' : field, conf, null);
                }
            });
    }

    loadFillings(data) {
        this.allFillings = core.copy(data.fillings) || [];
        this.fillingsCategories = core.copy(data.fillingsCategory) || [];
        this.glassTypes = isArray(data.glassTypes) ? core.copy(data.glassTypes) : [];
        this.glassTypeVariants = isArray(data.glassTypeVariants)
            ? core.copy(data.glassTypeVariants)
            : [];
    }

    async addGlazingUnit(filling = null) {
        let [newGlazingUnit, producerId] = await this.glazingUnitsService.openGlazingUnitModal(
            filling ? <GlazingUnitElement[]>core.copy(filling.glazing_unit) : null,
            filling ? Number(filling.fillings_producer_id) : null
        );
        if (newGlazingUnit) {
            const newGlazingUnitJson = JSON.stringify(newGlazingUnit);
            if (filling && JSON.stringify(filling.glazing_unit) == newGlazingUnitJson) {
                return false;
            } else {
                const matchedFillings = this.allFillings.filter(
                    el => el.glazing_unit && JSON.stringify(el.glazing_unit) == newGlazingUnitJson
                );

                if (matchedFillings.length) {
                    return matchedFillings[0];
                } else {
                    let newFilling = this.glazingUnitsService.newFillingFromGlazingUnit(
                        newGlazingUnit,
                        producerId,
                        filling
                    );
                    this.saveCustomFilling(newFilling);
                    this.allFillings.unshift(newFilling);
                    return newFilling;
                }
            }
        } else {
            return false;
        }
    }

    saveCustomFilling(filling) {
        this.fillings.unshift(filling);
        this.allFillings.unshift(filling);
        const customFillings = core.parseJson(localStorage.getItem('customFillings') || '[]');
        customFillings.push(filling);
        localStorage.setItem('customFillings', JSON.stringify(customFillings));
    }

    loadMatchingFillings(conf: WindowActiveConfiguration, validate = true) {
        this.validationService.indeterminate(conf, 'loadedFillings');
        if (this.validationService.isValidElements(conf, ['system', 'colors']) && this.loadedData) {
            if (conf.CustomFillings) {
                conf.CustomFillings = this.glazingUnitsService.fixGlazingUnits(conf.CustomFillings);
                conf.CustomFillings.forEach(customFilling => {
                    if (!this.allFillings.some(el => el.id === customFilling.id)) {
                        this.allFillings.unshift(customFilling);
                    }
                });
            }
            this.fillings = this.allFillings.filter(filling => {
                const systemTypes = filling.system_types;
                const systemIds = filling.system_ids;
                return (
                    (filling.conf.length > 0
                        || filling.type === 'panels_glazing'
                        || this.isDoorPanel(filling))
                    && (filling.type === 'panels_glazing'
                        && !this.config().IccConfig.Configurators.glazingPanelsAvailabilityDependingOnProductSystem
                        || (Common.isArray(systemTypes)
                        && Common.isArray(systemIds)
                        && systemIds.indexOf(conf.System.id) > -1
                        && systemTypes.indexOf(conf.System.type) > -1))
                );
            });
            if (validate) {
                if (this.fillings.length === 0) {
                    this.validationService.invalid(conf, 'loadedFillings');
                    this.issuesService.registerDataProblem(
                        'no-matching-fillings',
                        'Brak pasujących wypełnień',
                        conf,
                        {
                            level: IssueLevel.FATAL,
                            extra: {
                                systemId: conf.System.id,
                                systemName: conf.System.name,
                                systemType: conf.System.type,
                            },
                        }
                    );
                } else {
                    Object.assign(conf, this.validationService.valid(conf, 'loadedFillings'));
                    this.issuesService.unregister('no-matching-fillings', conf);
                }
                this.eventBusService.post({
                    key: 'loadedFillings',
                    value: this.fillings,
                    conf,
                });
            }
        }
    }

    setDefaultGlassForSystem(conf, defaultConf) {
        conf.Sashes.forEach((sash) => {
            this.setDefaultFillingInSash(sash, sash, conf, defaultConf);
            const defaultFilling = this.getDefaultGlazingForSystem(sash, sash, conf, defaultConf);
            this.setFillingInSash(sash, sash, defaultFilling, conf, true);

            sash.intSashes.forEach((field) => {
                this.setDefaultFillingInSash(field, sash, conf, defaultConf);
                const flng = this.getDefaultGlazingForSystem(field, sash, conf, defaultConf);
                this.setFillingInSash(field, sash, flng, conf, true);
            });
        });
    }

    getDefaultGlazingForSystem(field, sash, conf: WindowActiveConfiguration, defaultConf) {
        const matchingFillings = this.getMatchingFillingsInSash(field, sash, conf);
        const defaultGlazingForSystem = matchingFillings.find(
            o => {
                if (conf.System.default_market_configuration?.glass_type_id) {
                    return Number(o.id.split('.')[0]) === Number(conf.System.default_market_configuration.glass_type_id);
                } else {
                    return Number(o.id.split('.')[0]) === Number(conf.System.default_glass_type_id);
                }
            }
        );

        return defaultGlazingForSystem;
    }


    validateFillingsAndFixIssues(conf: WindowActiveConfiguration, defaultConf, validateSideLight = false) {
        this.validationService.indeterminate(conf, 'fillings');
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
        ) {
            conf.Issues = conf.Issues.filter(
                i => i.key.indexOf(`no-matching-fillings-in-sash`) === -1
            );
            this.issuesService.unregister('invalid-fillings', conf);
            const pauseId = this.eventBusService.pause(['setGlazingInSash', 'setFillingColor', 'setPanelGlazing', 'setPanelTrim']);
            let valid = true;
            try {
                conf.Sashes.forEach(sash => {
                    if (!this.validFillingInSash(sash, sash, conf, validateSideLight) || this.fillingMapAvailable(sash, sash,  conf)) {
                        this.setDefaultFillingInSash(sash, sash, conf, defaultConf, true);
                        valid = false;
                    }
                    sash.intSashes.forEach(field => {
                        if (!this.validFillingInSash(field, sash, conf) || this.fillingMapAvailable(field, sash,  conf)) {
                            this.setDefaultFillingInSash(field, sash, conf, defaultConf, true);
                            valid = false;
                        }
                    });
                });
                if (valid) {
                    this.validationService.valid(conf, 'fillings');
                    this.issuesService.unregister('invalid-fillings', conf);
                } else {
                    this.validationService.invalid(conf, 'fillings');
                }
            } finally {
                this.eventBusService.resume(['setGlazingInSash', 'setFillingColor', 'setPanelGlazing', 'setPanelTrim'], pauseId);
            }
        }
    }

    fillingMapAvailable(field, sash, conf, skipIfNotDefault = false) {
        if(!skipIfNotDefault && (!this.config().IccConfig.Configurators.window.fillingsMapping || !field.glazing?.isDefault)) {
            return false;
        }
        const thermalTransmittanceFilling = this.getFillingBaseOnMappingThermalTransmittance(field, sash, conf);
        const thicknessFilling = this.getFillingBaseOnMappingThickness(field, sash, conf, thermalTransmittanceFilling);
        const hasThicknessFilling = thicknessFilling ? thicknessFilling.id !== field.glazing.id : false;
        return hasThicknessFilling;
    }

    getFillingBaseOnMappingThermalTransmittance(field, sash, conf) {
        const defaultGlazing = field.defaultGlazing;
        const hasMapping = defaultGlazing?.filling_maps?.thermal_transmittance?.length || false

        if(!defaultGlazing || !hasMapping) {
            return null
        }

        const thermalTransmittanceFillingsId = [defaultGlazing.id.split('.')[0], ...defaultGlazing.filling_maps.thermal_transmittance];

        const matchingFillingThicknessInSash = this.glazingBeadsService.getMatchingFillingThicknessInSash(field, sash, conf);
        for(const fillingId of thermalTransmittanceFillingsId) {
            const filling = this.fillings.find(f => Number(f.id.split('.')[0]) === Number(fillingId) && f.type === 'glazing');
            if(filling) {
                const match = matchingFillingThicknessInSash.some(
                    range =>
                        Number(filling.thickness_mm) >= range.min
                        && Number(filling.thickness_mm) <= range.max);
                if(match) {
                    return filling
                }
            }
        }
        return null;
    }

    getFillingBaseOnMappingThickness(field, sash, conf, thermalTransmittanceFilling) {
        const defaultGlazing = thermalTransmittanceFilling ?? field.defaultGlazing;
        const hasMapping = defaultGlazing?.filling_maps?.thickness?.length || false
        if((!defaultGlazing || !hasMapping) && !thermalTransmittanceFilling) {
            return null;
        }
        const defaultGlazingId = defaultGlazing.id.split('.')[0];
        let systemDefaultGlazing = 0;
        if(field.glazing.isDefault && Number(conf.System.default_glass_type_id) !== Number(defaultGlazingId)) {
            systemDefaultGlazing = conf.System.default_glass_type_id;
        }
        const thicknessFillingsId = [systemDefaultGlazing, defaultGlazingId, ...defaultGlazing?.filling_maps?.thickness || [], ...field.defaultGlazing?.filling_maps?.thickness || []];

        field = field.intSashes.length > 0 ? field.intSashes[0] : field;

        const glazingRestricts = this.configuratorsDataService.data.glazingRestrictions;

        if(!field.glazingSizes) {
            return null;
        }
        const glazingWidth = field.glazingSizes?.width || 0;
        const glazingHeight = field?.glazingSizes?.height || 0;
        const glazingArea = field?.glazingSizes.area || 0;
        const matchingFillingThicknessInSash = this.glazingBeadsService.getMatchingFillingThicknessInSash(field, sash, conf);
        for(const fillingId of thicknessFillingsId) {
            const filling = this.fillings.find(f => Number(f.id.split('.')[0]) === Number(fillingId) && f.type === 'glazing');
            if(filling) {
                const match = this.checkGlazing(filling, glazingRestricts, glazingWidth, glazingHeight, glazingArea)
                    && matchingFillingThicknessInSash.some(
                        range =>
                            Number(filling.thickness_mm) >= range.min
                            && Number(filling.thickness_mm) <= range.max);;
                if(match) {
                    return filling
                }
            }
        }
        return null;
    }

    filterFillingsByMapping(fillings: Filling[], conf: WindowActiveConfiguration, field?: ActiveSash, sash?: ActiveSash) {
        let matchingFillingThicknessInSash = [];
        const fillingDrawData = this.configurationsService.conf.Current.drawData.filling.filter(f => !field || f.sashId === field?.id).map(drawData => ({
            width: drawData.rect.width,
            height: drawData.rect.height,
            area: drawData.rectArea,
        }));
        if(field) {
           matchingFillingThicknessInSash = this.glazingBeadsService.getMatchingFillingThicknessInSash(field, sash, conf);
        } else {
            conf.Sashes.forEach(s => {
                if(s.intSashes.length) {
                    s.intSashes.forEach(f => {
                        const matchingFillingThicknessInCurrentSash = this.glazingBeadsService.getMatchingFillingThicknessInSash(f, s, conf);
                        if(!matchingFillingThicknessInSash.length) {
                            matchingFillingThicknessInSash = matchingFillingThicknessInCurrentSash;
                        } else {
                            matchingFillingThicknessInSash = matchingFillingThicknessInSash.filter(range1 => matchingFillingThicknessInCurrentSash.some(rage2 => range1.min === rage2.min && range1.max === rage2.max));
                        }
                    });
                } else {
                    const matchingFillingThicknessInCurrentSash = this.glazingBeadsService.getMatchingFillingThicknessInSash(s, s, conf);
                    if(!matchingFillingThicknessInSash.length) {
                        matchingFillingThicknessInSash = matchingFillingThicknessInCurrentSash;
                    } else {
                        matchingFillingThicknessInSash = matchingFillingThicknessInSash.filter(range1 => matchingFillingThicknessInCurrentSash.some(rage2 => range1.min === rage2.min && range1.max === rage2.max));
                    }
                }
            });
        }

        return fillings.filter(filling =>
            matchingFillingThicknessInSash.some(range => Number(filling.thickness_mm) >= range.min && Number(filling.thickness_mm) <= range.max)
            && (fillingDrawData.length === 0 || fillingDrawData.some(drawData => this.checkGlazing(filling, this.configuratorsDataService.data.glazingRestrictions, drawData.width, drawData.height, drawData.area)))
        );
    }

    validatePanelGlazingsAndFixIssues(conf: WindowActiveConfiguration) {
        this.validationService.indeterminate(conf, 'panelGlazings');
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
        ) {
            const pauseId = this.eventBusService.pause(['setPanelGlazing']);
            let valid = true;
            try {
                conf.Sashes.forEach(sash => {
                    if (!this.isValidPanelGlazing(sash)) {
                        this.setDefaultPanelGlazingInSash(sash);
                        valid = false;
                    }
                    sash.intSashes.forEach(field => {
                        if (!this.isValidPanelGlazing(field)) {
                            this.setDefaultPanelGlazingInSash(field);
                            valid = false;
                        }
                    });
                });
                if (valid) {
                    this.validationService.valid(conf, 'panelGlazings');
                } else {
                    this.validationService.invalid(conf, 'panelGlazings');
                }
            } finally {
                this.eventBusService.resume(['setPanelGlazing'], pauseId);
            }
        }
    }

    validatePanelTrimsAndFixIssues(conf: WindowActiveConfiguration) {
        this.validationService.indeterminate(conf, 'panelTrims');
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
        ) {
            const pauseId = this.eventBusService.pause(['setPanelTrim']);
            let valid = true;
            try {
                conf.Sashes.forEach(sash => {
                    if (!this.isValidPanelTrim(sash, 'outside')) {
                        this.setDefaultPanelTrimInSash(sash, conf, 'outside');
                        valid = false;
                    }
                    if (!this.isValidPanelTrim(sash, 'inside')) {
                        this.setDefaultPanelTrimInSash(sash, conf, 'inside');
                        valid = false;
                    }
                    sash.intSashes.forEach(field => {
                        if (!this.isValidPanelTrim(field)) {
                            this.setDefaultPanelTrimInSash(field, conf);
                            valid = false;
                        }
                        if (!this.isValidPanelTrim(field, 'inside')) {
                            this.setDefaultPanelTrimInSash(field, conf, 'inside');
                            valid = false;
                        }
                    });
                });
                if (valid) {
                    this.validationService.valid(conf, 'panelTrims');
                } else {
                    this.validationService.invalid(conf, 'panelTrims');
                }
            } finally {
                this.eventBusService.resume(['setPanelTrim'], pauseId);
            }
        }
    }

    validateFillings(conf: WindowActiveConfiguration, defaultConf) {
        this.validationService.indeterminate(conf, 'fillings');
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
        ) {
            const valid = conf.Sashes.every(
                sash =>
                    this.validFillingInSash(sash, sash, conf)
                    && sash.intSashes.every(field => this.validFillingInSash(field, sash, conf))
            );
            if (!valid) {
                this.validationService.invalid(conf, 'fillings');
                this.issuesService.simpleRegister(
                    'invalid-fillings',
                    'Niepoprawne wypełnienia!',
                    this.translateService.instant('WINDOW|Niepoprawne wypełnienia!'),
                    conf,
                    {
                        level: IssueLevel.ERROR,
                        logLevel: IssueLevel.INFO,
                        blockStepsAfter: 'glazing',
                    }
                );
            } else {
                Object.assign(conf, this.validationService.valid(conf, 'fillings'));
                this.issuesService.unregister('invalid-fillings', conf);
            }
            this.eventBusService.post({
                key: 'validatedFillings',
                value: valid,
            });
        }
    }

    validFillingInSash(field, sash, conf: WindowActiveConfiguration, validateSideLight = false) {
        if (
            !sash.glazing
            || !sash.glazing.id
            || (sash.type.type === 'F' && this.isDoorPanel(field.glazing))
        ) {
            return false;
        }

        const filling = field.glazing;

        const matchingThickness = this.glazingBeadsService.getMatchingFillingThicknessInSash(
            field,
            sash,
            conf,
            filling.type === 'deco_panels' ? filling.panel_type : 'panel_type_inset'
        );

        const systemTypes = filling.system_types;
        const systemIds = filling.system_ids;
        const overlayThickness = this.getDecoPanelOverlayThickness(filling);

        const alwaysMatchProfile = this.isAlwaysMatchProfile(filling);

        const activeSash = conf.Sashes.find(s => ['DRA', 'DOA'].indexOf(s.type.type) !== -1);
        const glazingIds = activeSash?.glazing?.glazing_ids || [];
        const notDoorSash = conf.type !== 'door'
            || ((conf as DoorActiveConfiguration).Model
            && (conf as DoorActiveConfiguration).Model.standard_fills)
            || ['DRA', 'DRP', 'DOA', 'DOP'].indexOf(sash.type.type) === -1;
        const frame = conf.Frames.find(f => f.id === sash.frameId);
        const isDoorLight = conf.type === 'door' && notDoorSash;
        const isSideLight = isDoorLight && frame.y + sash.ry + sash.rHeight === conf.Height;
        let sideLightCorrect = true;
        if(isSideLight && ((filling.glazing_to_filter && glazingIds.length === 0) || glazingIds.length > 0) && validateSideLight) {
            sideLightCorrect = false;
        }
        const defaultConf =  this.configurationsService.conf.Default;
        const defaultGlazingForSash = this.getDefaultFillingInSash(field, sash, conf, defaultConf, false);

        const ifDoorPanel = this.isDoorPanel(filling)
            && ((filling.type === 'deco_panels'
            && (field.intSashes.length
                || this.isDecoPanelMatchToDimensions(filling, field, sash, conf)
                && (alwaysMatchProfile || matchingThickness.some(
                    range =>
                        Number(filling.thickness_mm) - overlayThickness >= range.min
                        && Number(filling.thickness_mm) - overlayThickness <= range.max
                ))
            ))
            || (filling.type === 'door_panels'
                && this.isDoorPanelMatchToDoorSizes(
                    filling,
                    sash,
                    conf as DoorActiveConfiguration
                )));

        let glazingRestrictionsCorrect = true;
        if(this.config().IccConfig.Configurators.window.fillingsMapping && this.fillingMapAvailable(field, sash, conf, true)) {
            if(field.glazingSizes) {
                const glazingWidth = field.glazingSizes?.width || 0 ;
                const glazingHeight = field?.glazingSizes?.height || 0;
                const glazingArea = field?.glazingSizes.area || 0;
                glazingRestrictionsCorrect = this.checkGlazing(filling, this.configuratorsDataService.data.glazingRestrictions, glazingWidth, glazingHeight, glazingArea);
            }
        }

        const ifNotDoorPanel = !this.isDoorPanel(filling)
            && filling.type !== 'panels_glazing'
            && matchingThickness.some(
                range =>
                    Number(filling.thickness_mm) >= range.min
                    && Number(filling.thickness_mm) <= range.max
            )
            && this.isFillingMatchToSashType(conf, sash, filling)
            && sideLightCorrect
            && (defaultGlazingForSash && field.glazing?.id && field.glazing?.isDefault ? String(defaultGlazingForSash.id) === String(field.glazing?.id) : true)
            && glazingRestrictionsCorrect;

        return (
            (ifDoorPanel || ifNotDoorPanel)
            && Common.isArray(systemTypes)
            && Common.isArray(systemIds)
            && systemIds.indexOf(conf.System.id) > -1
            && systemTypes.indexOf(conf.System.type) > -1
        );
    }

    private getDecoPanelOverlayThickness(filling: any) {
        let overlayThickness = 0;
        if (filling.panel_type === 'panel_type_double') {
            overlayThickness = 2 * (filling.overlay_thickness || 0);
        }
        else if (filling.panel_type === 'panel_type_inner' || filling.panel_type === 'panel_type_outer') {
            overlayThickness = filling.overlay_thickness || 0;
        }
        return overlayThickness;
    }

    private isFillingMatchToSashType(conf: WindowActiveConfiguration, sash: ActiveSash, filling: IccFilling): any {
        const notDoorSash = conf.type !== 'door'
            || ((conf as DoorActiveConfiguration).Model
            && (conf as DoorActiveConfiguration).Model.standard_fills)
            || ['DRA', 'DRP', 'DOA', 'DOP'].indexOf(sash.type.type) === -1;
        const isDoorLight = conf.type === 'door' && notDoorSash;
        const frame = conf.Frames.find(f => f.id === sash.frameId);
        const isSideLight = isDoorLight && frame.y + sash.ry + sash.rHeight === conf.Height;
        const isTopLight = isDoorLight && frame.y + sash.ry + sash.rHeight < conf.Height;
        if(isSideLight) {
            const activeSash = conf.Sashes.find(s => ['DRA', 'DOA'].indexOf(s.type.type) !== -1);
            const glazingIds = activeSash?.glazing?.glazing_ids?.map(id => Number(id)) || [];
            if(filling.glazing_to_filter && !glazingIds.includes(Number(filling.id.toString().split('.')[0]))) {
                return false;
            }
        }
        return (
            (
                notDoorSash
                && filling.conf.indexOf('window') > -1
            ) || isSideLight && filling.conf.indexOf('side-light') > -1
            || isTopLight && filling.conf.indexOf('top-light') > -1
            || filling.conf.indexOf('door') > -1
        );
    }

    checkSingleFilling(
        conf: WindowActiveConfiguration | DoorActiveConfiguration,
        defaultConf: any
    ) {
        let isSingleFilling = true;
        let isSingleFillingInWindowSashes = true;
        let isSingleFillingInDoorActiveSashes = true;
        let isSingleInnerPanelInDoorActiveSashes = true;
        let isSingleFillingInDoorPassiveSashes = true;
        let isSinglePassivePanelInDoorPassiveSashes = true;
        let isSingleFillingInDoorLightSashes = true;
        let isSingleFillingInDoorTopLightSashes = true;
        let isSingleFillingInDoorLeftLightSashes = true;
        let isSingleFillingInDoorRightLightSashes = true;
        let firstFillingId: string | null = null;
        let firstDefaultFillingId: string | null = null;
        let firstFillingInWindowSashesId: string | null = null;
        let firstFillingInDoorActiveSashesId: string | null = null;
        let firstInnerPanelInDoorActiveSashesId: string | null = null;
        let firstFillingInDoorPassiveSashesId: string | null = null;
        let firstInnerFillingInDoorPassiveSashesId: string | null = null;
        let firstFillingInDoorLightSashesId: string | null = null;
        let firstFillingInDoorTopLightSashesId: string | null = null;
        let firstFillingInDoorLeftLightSashesId: string | null = null;
        let firstFillingInDoorRightLightSashesId: string | null = null;
        let firstPanelType: 'Inset' | 'Outer' | 'Double' | 'Inner' | 'Mixed' = 'Inset';
        let firstPanelGlazing: any = null;
        let firstPassivePanelGlazing: any = null;
        let firstGlazingIsDefault = false;
        let firstPanelTrim: any = null;
        let firstPassivePanelTrim: any = null;
        let firstInnerPanelTrim: any = null;
        let firstPassiveInnerPanelTrim: any = null;

        conf.Sashes.forEach((sash, i) => {
            let isSingleFillingInSash = true;
            let firstFillingIdInSash = sash.glazing.id || null;

            isSingleFillingInSash = sash.intSashes.every((intSash, j) => {
                if (j === 0) {
                    firstFillingIdInSash = intSash.glazing.id || null;
                }
                return firstFillingIdInSash === intSash.glazing.id;
            });

            if (i === 0) {
                firstFillingId = firstFillingIdInSash;
                firstGlazingIsDefault = sash.glazing.isDefault || false;
                firstDefaultFillingId = sash.defaultGlazing?.id || null
            }

            if (
                i === 1 &&
                conf.Layout?.sashes.length > 1 &&
                conf.Layout?.sashes.every(s => s?.type === conf.Layout?.sashes[0]?.type)
            ) {
                if (conf.Sashes[0]?.panelGlazing?.id) {
                    sash.panelGlazing = conf.Sashes[0]?.panelGlazing;
                }
                if (conf.Sashes[0]?.panelTrim?.id) {
                    sash.panelTrim = conf.Sashes[0]?.panelTrim;
                }
                if (conf.Sashes[0]?.innerPanelTrim?.id) {
                    sash.innerPanelTrim = conf.Sashes[0]?.innerPanelTrim;
                }
            }

            if (sash.type.type === 'DRA' || sash.type.type === 'DOA') {
                if (!firstFillingInDoorActiveSashesId) {
                    firstFillingInDoorActiveSashesId = firstFillingIdInSash;
                }
                if (!firstInnerPanelInDoorActiveSashesId) {
                    firstInnerPanelInDoorActiveSashesId =
                        (sash.panelInner && sash.panelInner.id) || null;
                }
                isSingleFillingInDoorActiveSashes =
                    isSingleFillingInDoorActiveSashes
                    && isSingleFillingInSash
                    && firstFillingInDoorActiveSashesId === firstFillingIdInSash;

                isSingleInnerPanelInDoorActiveSashes =
                    isSingleInnerPanelInDoorActiveSashes
                    && (!sash.panelInner
                        || firstInnerPanelInDoorActiveSashesId === sash.panelInner.id);
                firstPanelType = sash.panelType;
                firstPanelGlazing = core.copy(sash.panelGlazing);
                firstPanelTrim = core.copy(sash.panelTrim);
                firstInnerPanelTrim = core.copy(sash.innerPanelTrim);
            } else if (sash.type.type === 'DRP' || sash.type.type === 'DOP') {
                if (!firstFillingInDoorPassiveSashesId) {
                    firstFillingInDoorPassiveSashesId = firstFillingIdInSash;
                }
                if (!firstInnerFillingInDoorPassiveSashesId) {
                    firstInnerFillingInDoorPassiveSashesId =
                        (sash.panelInner && sash.panelInner.id) || null;
                }
                isSingleFillingInDoorPassiveSashes = Boolean(
                    firstFillingInDoorPassiveSashesId
                        && isSingleFillingInSash
                        && firstFillingInDoorPassiveSashesId === firstFillingIdInSash
                );
                isSinglePassivePanelInDoorPassiveSashes =
                    isSinglePassivePanelInDoorPassiveSashes
                    && (!sash.panelInner
                        || firstInnerFillingInDoorPassiveSashesId === sash.panelInner.id);
                firstPassivePanelGlazing = core.copy(sash.panelGlazing);
                firstPassivePanelTrim = core.copy(sash.panelTrim);
                firstPassiveInnerPanelTrim = core.copy(sash.innerPanelTrim);
            } else {
                if (!firstFillingInWindowSashesId) {
                    firstFillingInWindowSashesId = firstFillingIdInSash;
                }
                isSingleFillingInWindowSashes =
                    isSingleFillingInWindowSashes
                    && isSingleFillingInSash
                    && firstFillingInWindowSashesId === firstFillingIdInSash;
            }

            if (sash.type.type === 'F') {
                const frame = conf.Frames.find(f => f.id === sash.frameId);
                if (sash.nearMullions.left === -1 && ((frame && frame.x) || 0) + sash.rx === 0) {
                    if (!firstFillingInDoorLightSashesId) {
                        firstFillingInDoorLightSashesId = firstFillingIdInSash;
                    }
                    if (
                        sash.nearMullions.top === -1
                        && ((frame && frame.x) || 0) + sash.rx + sash.rWidth === conf.Width
                    ) {
                        if (!firstFillingInDoorTopLightSashesId) {
                            firstFillingInDoorTopLightSashesId = firstFillingIdInSash;
                        }
                        isSingleFillingInDoorTopLightSashes =
                            isSingleFillingInDoorTopLightSashes
                            && isSingleFillingInSash
                            && firstFillingInDoorTopLightSashesId === firstFillingIdInSash;
                    } else {
                        if (!firstFillingInDoorLeftLightSashesId) {
                            firstFillingInDoorLeftLightSashesId = firstFillingIdInSash;
                        }
                        isSingleFillingInDoorLeftLightSashes =
                            isSingleFillingInDoorLeftLightSashes
                            && isSingleFillingInSash
                            && firstFillingInDoorLeftLightSashesId === firstFillingIdInSash;
                    }
                    isSingleFillingInDoorLightSashes =
                        isSingleFillingInDoorLightSashes
                        && firstFillingInDoorLightSashesId === firstFillingIdInSash;
                } else if (
                    sash.nearMullions.right === -1
                    && ((frame && frame.x) || 0) + sash.rx > 0
                ) {
                    if (!firstFillingInDoorLightSashesId) {
                        firstFillingInDoorLightSashesId = firstFillingIdInSash;
                    }
                    if (!firstFillingInDoorRightLightSashesId) {
                        firstFillingInDoorRightLightSashesId = firstFillingIdInSash;
                    }
                    isSingleFillingInDoorRightLightSashes =
                        isSingleFillingInDoorRightLightSashes
                        && isSingleFillingInSash
                        && firstFillingInDoorRightLightSashesId === firstFillingIdInSash;
                    isSingleFillingInDoorLightSashes =
                        isSingleFillingInDoorLightSashes
                        && firstFillingInDoorLightSashesId === firstFillingIdInSash;
                }
            }

            sash.oneGlazing = isSingleFillingInSash;
            isSingleFilling =
                isSingleFilling && isSingleFillingInSash && firstFillingId === firstFillingIdInSash;
        });

        conf.OneGlazing = isSingleFilling;
        defaultConf.OneGlazing = isSingleFilling;
        if (isSingleFilling) {
            defaultConf.Glazing = { id: firstFillingId, isDefault: firstGlazingIsDefault };
        }
        conf.OneFilling = {
            window: false,
            defaultWindow: false,
            doorActive: false,
            doorActiveInner: false,
            doorPassive: false,
            doorPassiveInner: false,
            doorLight: false,
            doorTopLight: false,
            doorLeftLight: false,
            doorRightLight: false,
        };
        conf.OneFilling.defaultWindow = firstDefaultFillingId || false;

        if (isSingleFillingInWindowSashes) {
            conf.OneFilling.window = firstFillingInWindowSashesId || false;
        }
        if (isSingleFillingInDoorActiveSashes) {
            conf.OneFilling.doorActive = firstFillingInDoorActiveSashesId || false;
            if (DoorActiveConfiguration.is(conf)) {
                const filling = this.getFillingFromId(firstFillingInDoorActiveSashesId || '');
                const activeSashHasLayout = conf.Sashes.some(
                    s => (s.type.type === 'DRA' || s.type.type === 'DOA')
                        && s.intSashes
                        && s.intSashes.length > 1
                );
                if (filling && this.isDoorPanel(filling) && !activeSashHasLayout) {
                    conf.Model = filling;
                    conf.ModelOptions.panelType = firstPanelType;
                    conf.ModelOptions.panelGlazing = firstPanelGlazing;
                    conf.ModelOptions.panelTrim = firstPanelTrim;
                    conf.ModelOptions.innerPanelTrim = firstInnerPanelTrim;

                    const innerFilling = this.getFillingFromId(
                        firstInnerPanelInDoorActiveSashesId || ''
                    );
                    if (innerFilling) {
                        conf.ModelOptions.panelInner = core.copy(innerFilling);
                    }
                }
            }
        }
        if (isSingleInnerPanelInDoorActiveSashes) {
            conf.OneFilling.doorActiveInner = firstInnerPanelInDoorActiveSashesId || false;
        }
        if (isSinglePassivePanelInDoorPassiveSashes) {
            conf.OneFilling.doorPassiveInner = firstInnerFillingInDoorPassiveSashesId || false;
        }
        if (isSingleFillingInDoorPassiveSashes) {
            conf.OneFilling.doorPassive = firstFillingInDoorPassiveSashesId || false;
            if (DoorActiveConfiguration.is(conf)) {
                const filling = this.getFillingFromId(firstFillingInDoorPassiveSashesId || '');
                const passiveSashHasLayout = conf.Sashes.some(
                    s => (s.type.type === 'DRP' || s.type.type === 'DOP')
                        && s.intSashes
                        && s.intSashes.length > 1
                );
                if (filling && this.isDoorPanel(filling) && !passiveSashHasLayout) {
                    conf.ModelOptions.passiveSashModel = filling;
                    conf.ModelOptions.passivePanelGlazing = firstPassivePanelGlazing;
                    conf.ModelOptions.passivePanelTrim = firstPassivePanelTrim;
                    conf.ModelOptions.passiveInnerPanelTrim = firstPassiveInnerPanelTrim;
                }

                const innerFilling = this.getFillingFromId(
                    firstInnerFillingInDoorPassiveSashesId || ''
                );
                if (innerFilling) {
                    conf.ModelOptions.passiveInnerSashModel = core.copy(innerFilling);
                }
            }
        }
        if (isSingleFillingInDoorLightSashes) {
            conf.OneFilling.doorLight = firstFillingInDoorLightSashesId || false;
        }
        if (isSingleFillingInDoorTopLightSashes) {
            conf.OneFilling.doorTopLight = firstFillingInDoorTopLightSashesId || false;
        }
        if (isSingleFillingInDoorLeftLightSashes) {
            conf.OneFilling.doorLeftLight = firstFillingInDoorLeftLightSashesId || false;
        }
        if (isSingleFillingInDoorRightLightSashes) {
            conf.OneFilling.doorRightLight = firstFillingInDoorRightLightSashesId || false;
        }

        this.eventBusService.post({
            key: 'icc-redraw',
            value: {},
        });
    }

    checkSingleFillingColors(
        conf: WindowActiveConfiguration | DoorActiveConfiguration,
        defaultConf: any
    ) {
        if (DoorActiveConfiguration.is(conf)) {
            const doorSash = this.getDoorSash(conf, 'doorActive');
            if (doorSash) {
                conf.ModelOptions.selectedColor =
                    (doorSash.glazing
                        && doorSash.glazing.selectedColor)
                    || {};
                conf.ModelOptions.selectedColorSecond =
                    (doorSash.glazing
                        && doorSash.glazing.selectedColorSecond)
                    || {};

                conf.ModelOptions.innerSelectedColor =
                    (doorSash.panelInner
                        && doorSash.panelInner.selectedColor)
                    || {};
                conf.ModelOptions.innerSelectedColorSecond =
                    (doorSash.panelInner
                        && doorSash.panelInner.selectedColorSecond)
                    || {};
            }

            const doorPassiveSash = this.getDoorSash(conf, 'doorPassive');
            if (doorPassiveSash) {
                conf.ModelOptions.passiveSelectedColor =
                    (doorPassiveSash.glazing
                        && doorPassiveSash.glazing.selectedColor)
                    || {};
                conf.ModelOptions.passiveSelectedColorSecond =
                    (doorPassiveSash.glazing
                        && doorPassiveSash.glazing.selectedColorSecond)
                    || {};

                conf.ModelOptions.passiveInnerSelectedColor =
                    (doorPassiveSash.panelInner
                        && doorPassiveSash.panelInner.selectedColor)
                    || {};
                conf.ModelOptions.passiveInnerSelectedColorSecond =
                    (doorPassiveSash.panelInner
                        && doorPassiveSash.panelInner.selectedColorSecond)
                    || {};
            }
        }
    }

    getMatchingFillingsForModal(
        field,
        conf: WindowActiveConfiguration,
        sash?,
    ): {matchingFillings: any[], selectedFilling: string | null, bondedGlazing: boolean, noDivInSash: boolean, oneGlazing: boolean} {
        let matchingFillings = [];
        let selectedFilling: string | null = null;
        let bondedGlazing = false;
        let noDivInSash = true;
        let oneGlazing = true;
        if (typeof field === 'undefined' || field === 'default') {
            matchingFillings = this.getMatchingFillings(conf);
            if(this.config().IccConfig.Configurators.window.fillingsMapping) {
                matchingFillings = this.filterFillingsByMapping(matchingFillings, conf);
            }
            selectedFilling = conf.Sashes[0].glazing.id;
            oneGlazing = conf.OneGlazing
            bondedGlazing = conf.Sashes[0].bondedGlazing;
        } else if (field === 'window') {
            matchingFillings = this.getMatchingFillingsForWindowSashes(conf);
            selectedFilling = conf.OneFilling.window || null;
            bondedGlazing = conf.OneBondedGlazingPerSash.window;
        } else if (field === 'doorActive') {
            matchingFillings = this.getMatchingFillingsForDoorSashes(conf, 'doorActive');
            selectedFilling =
                conf.OneFilling.doorActive
                || conf.Sashes.find(s => ['DRA', 'DOA'].includes(s.type.type)).glazing.id
                || null;
            bondedGlazing = conf.OneBondedGlazingPerSash.doorActive;
            noDivInSash = conf.Sashes.filter(s => ['DRA', 'DOA'].includes(s.type.type)).every(
                s => s.intSashes.length < 2
            );
        } else if (field === 'doorPassive') {
            matchingFillings = this.getMatchingFillingsForDoorSashes(conf, 'doorPassive');
            selectedFilling =
                conf.OneFilling.doorPassive
                || conf.Sashes.find(s => ['DRP', 'DOP'].includes(s.type.type)).glazing.id
                || null;
            bondedGlazing = conf.OneBondedGlazingPerSash.doorPassive;
            noDivInSash = conf.Sashes.filter(s => ['DRP', 'DOP'].includes(s.type.type)).every(
                s => s.intSashes.length < 2
            );
        } else if (field === 'doorTopLight') {
            matchingFillings = this.getMatchingFillingsForDoorSashes(conf, 'doorTopLight');
            selectedFilling = conf.OneFilling.doorTopLight || null;
            bondedGlazing = conf.OneBondedGlazingPerSash.doorTopLight;
        } else if (field === 'doorLeftLight') {
            matchingFillings = this.getMatchingFillingsForDoorSashes(conf, 'doorLeftLight');
            selectedFilling = conf.OneFilling.doorLeftLight || null;
            bondedGlazing = conf.OneBondedGlazingPerSash.doorLeftLight;
        } else if (field === 'doorRightLight') {
            matchingFillings = this.getMatchingFillingsForDoorSashes(conf, 'doorRightLight');
            selectedFilling = conf.OneFilling.doorRightLight || null;
            bondedGlazing = conf.OneBondedGlazingPerSash.doorRightLight;
        } else {
            matchingFillings = this.getMatchingFillingsInSash(field, sash, conf);
            if(this.config().IccConfig.Configurators.window.fillingsMapping) {
                matchingFillings = this.filterFillingsByMapping(matchingFillings, conf, field, sash);
            }
            selectedFilling = field.glazing.id;
            bondedGlazing = field.bondedGlazing;
        }

        matchingFillings = this.getUniqueOrderedFillings(matchingFillings);

        return {matchingFillings, selectedFilling, bondedGlazing, noDivInSash, oneGlazing};
    }

    async openModal(
        field,
        conf: WindowActiveConfiguration,
        defaultConf: WindowActiveConfiguration,
        sash?,
        callback?
    ) {
        const {matchingFillings, selectedFilling, bondedGlazing, noDivInSash, oneGlazing} = this.getMatchingFillingsForModal(field, conf, sash);

        const user = this.userService.get();
        const modalInstance = this.modalService.open({
            templateUrl: 'modalGlazing.html',
            controller: 'ModalGlazingCtrl as mglazing',
            pageComponent: FillingsListPageComponent,
            resolve: {
                fillings: () => matchingFillings,
                fillingsCategories: () => this.fillingsCategories,
                glassTypes: () => this.glassTypes,
                glassTypeVariants: () => this.glassTypeVariants,
                modalData: () => this.modalGlassTypeData,
                selGlassType: () => this.getFillingFromId(selectedFilling),
                selectFor: () => sash || field,
                noDivInSash: () => noDivInSash,
                hasGlasses: () => this.hasGlasses,
                bondedGlazing: () => bondedGlazing,
                b2c: () => !user || !user.access || user.access === 'klient',
                onlyGlazingList: () =>
                    field === 'doorTopLight'
                    || field === 'doorLeftLight'
                    || field === 'doorRightLight',
                filters$: () =>
                    new BehaviorSubject<any>({
                        selectedFilling: null,
                        filters: {
                            thermalTransmittance: 0,
                            glassCount: 0,
                            securityLevelOutside: 0,
                            securityLevelInside: 0,
                            noiseProtection: 0,
                            glassType: '0',
                            glassTypeVariant: 0,
                        },
                        classicFilters: null,
                    }),
                addGlazingUnit: () => this.addGlazingUnit.bind(this),
                oneGlazing: () => oneGlazing
            },
        });

        const selectedData = await modalInstance.result;
        if (selectedData) {
            selectedData.glass.selected = true;
            if (
                DoorActiveConfiguration.is(conf)
                && (
                    typeof field === 'undefined'
                    || field === 'default'
                    || field === 'door'
                    || field === 'doorActive'
                )
            ) {
                this.clearPredefinedLayoutMeta(conf);
            }
            if (typeof field !== 'undefined' && Common.isString(field) && field !== 'default') {
                this.setFillingInFilteredSashes(
                    conf,
                    selectedData.glass,
                    false,
                    this.getFilterForField(field, conf)
                );
            } else if (typeof sash === 'undefined' || Common.isString(sash)) {
                this.setFillingInAllSashes(conf, selectedData.glass);
            } else {
                this.setFillingInSash(field, sash, selectedData.glass, conf);
            }
            if (
                selectedData.glass.custom
                && typeof core.fId(conf.CustomFillings, selectedData.glass.id) === 'undefined'
            ) {
                conf.CustomFillings.push(selectedData.glass);
                this.clearCustomFillings(conf, defaultConf);
            }

            if (typeof sash === 'undefined' || Common.isString(sash)) {
                this.bondedGlazingService.setBondedGlazingInAllSashes(
                    conf,
                    selectedData.bondedGlazing
                );
            } else {
                this.bondedGlazingService.setBondedGlazingInSash(
                    field,
                    sash,
                    selectedData.bondedGlazing
                );
            }

            this.modalGlassTypeData.glassTab = selectedData.glassTab;
            this.modalGlassTypeData.selectedSecurity = selectedData.selectedSecurity;
            this.modalGlassTypeData.selectedOrnament = selectedData.selectedOrnament;
            this.modalGlassTypeData.selectedUg = selectedData.selectedUg;
            this.modalGlassTypeData.selectedRw = selectedData.selectedRw;
            if (this.config().IccConfig.Configurators.dependencies) {
                this.eventBusService.post({ key: 'processDependencies', value: null });
            }
            this.eventBusService.post({
                key: 'changedFillings',
                value: {},
            });
            if (callback) {
                callback();
            }
        }

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    clearCustomFillings(conf: WindowActiveConfiguration, defaultConf: WindowActiveConfiguration) {
        const ids = [];

        conf.Sashes.forEach(sash => {
            if (sash.intSashes.length) {
                sash.intSashes.forEach(isash => {
                    if (
                        isash.glazing.custom
                        && typeof core.fId(ids, isash.glazing.id) === 'undefined'
                    ) {
                        ids.push(isash.glazing.id);
                    }
                });
            } else {
                if (sash.glazing.custom && typeof core.fId(ids, sash.glazing.id) === 'undefined') {
                    ids.push(sash.glazing.id);
                }
            }
        });

        conf.CustomFillings = this.glazingUnitsService.fixGlazingUnits(conf.CustomFillings.filter(el => ids.indexOf(el.id) > -1));
        defaultConf.CustomFillings = core.copy(conf.CustomFillings);
    }

    openModelModal(field, conf: DoorActiveConfiguration, defaultConf: DoorActiveConfiguration) {
        let matchingFillings = [];
        let selectedFilling: string | null = null;
        let noDivInSash = true;
        let fillingsViewSide = 'outer';
        if (field === 'doorActive') {
            matchingFillings = this.getMatchingFillingsForDoorSashes(conf, 'doorActive').filter(
                f => f.type === (conf.System.door_type ? 'door_panels' : 'deco_panels')
            );
            selectedFilling =
                conf.OneFilling.doorActive
                || conf.Sashes.find(s => ['DRA', 'DOA'].includes(s.type.type)).glazing.id
                || null;
            noDivInSash = conf.Sashes.filter(s => ['DRA', 'DOA'].includes(s.type.type)).every(
                s => s.intSashes.length < 2
            );
        } else if (field === 'doorActiveInner') {
            const outerFillingId = conf.Sashes.find(s => ['DRA', 'DOA'].includes(s.type.type))?.glazing?.id;
            matchingFillings = this.getMatchingInnerPanelsForDoorSashesFiltered(conf, true);
            if (conf.System.door_type) {
                matchingFillings = matchingFillings.filter(p => p.base_side === "outside" && Number(p.id) === Number(outerFillingId)
                || p.base_side === "any" || p.base_side === "inside");
            }
            selectedFilling =
                conf.OneFilling.doorActiveInner
                || conf.Sashes.find(s => ['DRA', 'DOA'].includes(s.type.type)).glazing.id
                || null;
            noDivInSash = conf.Sashes.filter(s => ['DRA', 'DOA'].includes(s.type.type)).every(
                s => s.intSashes.length < 2
            );
            fillingsViewSide = 'inner';
        } else if (field === 'doorPassive') {
            matchingFillings = this.getMatchingFillingsForDoorSashes(conf, 'doorPassive').filter(
                f => f.type === (conf.System.door_type ? 'door_panels' : 'deco_panels')
            );
            selectedFilling =
                conf.OneFilling.doorPassive
                || conf.Sashes.find(s => ['DRP', 'DOP'].includes(s.type.type)).glazing.id
                || null;
            noDivInSash = conf.Sashes.filter(s => ['DRP', 'DOP'].includes(s.type.type)).every(
                s => s.intSashes.length < 2
            );
        } else if (field === 'doorPassiveInner') {
            const outerFillingId = conf.Sashes.find(s => ['DRP', 'DOP'].includes(s.type.type))?.glazing?.id;
            matchingFillings = this.getMatchingInnerPanelsForDoorSashesFiltered(conf, false);
            if (conf.System.door_type) {
                matchingFillings = matchingFillings.filter(p => p.base_side === "outside" && Number(p.id) === Number(outerFillingId)
                || p.base_side === "any" || p.base_side === "inside");
            }
            selectedFilling =
                conf.OneFilling.doorPassiveInner
                || conf.Sashes.find(s => ['DRP', 'DOP'].includes(s.type.type)).glazing.id
                || null;
            noDivInSash = conf.Sashes.filter(s => ['DRP', 'DOP'].includes(s.type.type)).every(
                s => s.intSashes.length < 2
            );
            fillingsViewSide = 'inner';
        }

        matchingFillings = this.getUniqueOrderedFillings(matchingFillings);
        const modalInstance = this.modalService.open({
            pageComponent: ModelListPageComponent,
            resolve: {
                fillings: () => matchingFillings,
                fillingsCategories: () => this.fillingsCategories,
                selectedFilling: () => this.getFillingFromId(selectedFilling),
                fillingsViewSide: () => fillingsViewSide,
                selectFor: () => field,
                noDivInSash: () => noDivInSash,
                hasGlasses: () => this.hasGlasses,
            },
        });

        modalInstance.result.then(selectedData => {
            if (selectedData) {
                if (typeof field !== 'undefined' && Common.isString(field)) {
                    this.setDecorativePanelInDoorSashes(
                        conf,
                        defaultConf,
                        selectedData.filling,
                        field
                    );
                } else {
                    if (DoorActiveConfiguration.is(conf)) {
                        this.clearPredefinedLayoutMeta(conf);
                    }
                    this.setFillingInAllSashes(conf, selectedData.filling);
                }

                if (this.config().IccConfig.Configurators.dependencies) {
                    this.eventBusService.post({ key: 'processDependencies', value: null });
                }
                this.eventBusService.post({
                    key: 'changedFillings',
                    value: {},
                });
            }
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    openModalPanelOptions(field, conf: DoorActiveConfiguration, sash?) {
        const fillingsData = this.getPanelGlazingsByFieldType(field, conf, sash);
        const matchingFillings = this.getMatchingFillingVariantsForField(conf, field, fillingsData['sash']);
        sash = fillingsData['sash'];

        const panelGlazings = fillingsData['panelGlazings'];
        const filling = fillingsData['filling'];
        const panelTrims = this.getPanelTrimsByFieldType(field, conf, sash, 'outside')?.panelTrims || [];
        const innerPanelTrims = this.getPanelTrimsByFieldType(field, conf, sash, 'inside')?.panelTrims || [];

        return this.modalService
            .open({
                templateUrl: 'modalDecoPanelOptions.html',
                controller: 'ModalDecoPanelOptionsCtrl as $ctrl',
                pageComponent: DecoPanelOptionsPageComponent,
                resolve: {
                    fillings: () => panelGlazings,
                    fillingsCategories: () => this.fillingsCategories,
                    modalData: () => this.modalGlassTypeData,
                    selGlazing: () => sash.panelGlazing,
                    panelTrim: () => sash.panelTrim,
                    innerPanelTrim: () => sash.innerPanelTrim,
                    panelType: () => sash.panelType,
                    filling: () => filling,
                    filteredFillings: () => matchingFillings,
                    panelTrims: () => panelTrims,
                    innerPanelTrims: () => innerPanelTrims,
                    openFillingModal: () => this.openModalPanelGlazings.bind(this),
                    changePanelTrim: () => (face) => this.changePanelTrim(field, conf, sash, face),
                    options$: () =>
                        new BehaviorSubject({
                            selGlazing: sash.panelGlazing,
                            panelType: sash.panelType,
                            filling: filling,
                            panelTrim: sash.panelTrim,
                            innerPanelTrim: sash.innerPanelTrim,
                        }),
                },
            })
            .result.then(selectedData => {
                if (selectedData) {
                    if (DoorActiveConfiguration.is(conf)) {
                        this.clearPredefinedLayoutMeta(conf);
                    }
                    if (field === 'door' && conf.Sashes.length === 0) {
                        conf.Model = selectedData.filling;
                        conf.ModelOptions.panelGlazing = selectedData.selGlazing;
                        conf.ModelOptions.panelType = selectedData.panelType;
                    } else {
                        selectedData.filling.selectedColor =
                            sash.intSashes[0].glazing.selectedColor;
                        selectedData.filling.selectedColorSecond =
                            sash.intSashes[0].glazing.selectedColorSecond;
                        if (field !== 'doorActiveInner') {
                            this.setFillingInSash(sash, sash, selectedData.filling, conf);
                        }
                        if (typeof field === 'undefined' || field === 'default') {
                            this.setPanelOptionsInAllSashes(
                                conf,
                                selectedData.selGlazing,
                                selectedData.panelType
                            );
                        } else {
                            this.setPanelGlazingInSash(sash, selectedData.selGlazing);
                            this.setPanelTypeInSash(sash, selectedData.panelType);
                            this.setDecoPanelDefaults(sash, sash, conf);
                        }
                    }
                }
            });
    }

    changePanelGlazing(field, conf: DoorActiveConfiguration, sash?) {
        const fillingsData = this.getPanelGlazingsByFieldType(field, conf, sash);
        const panelGlazings = fillingsData['panelGlazings'];
        const filling = fillingsData['filling'];
        sash = fillingsData['sash'];

        if (panelGlazings.length === 0) {
            return Promise.resolve();
        }
         else if (conf.System.door_type && field !== 'doorActiveInner') {
            return this.openModalPanelGlazings(panelGlazings, sash.panelGlazing, conf).then(
                result => {
                    if (result) {
                        if (field === 'door' && conf.Sashes.length === 0) {
                            conf.ModelOptions.panelGlazing = result.glazing;
                        } else {
                            if(field === 'doorPassive') {
                                conf.ModelOptions.passivePanelGlazing = result.glazing;
                            }
                            if (typeof field === 'undefined' || field === 'default') {
                                this.setPanelGlazingsInAllSashes(conf, result.glazing);
                            } else {
                                this.setPanelGlazingInSash(sash, result.glazing);
                                if (result?.glazing?.type !== 'panels_glazing') {
                                    this.setDecoPanelDefaults(sash, sash, conf);
                                }
                            }
                        }
                    }
                }
            );
        }
    }

    openModalPanelGlazings(
        panelGlazings: IccFilling[],
        selectedFilling: Partial<IccFilling>,
        conf: WindowActiveConfiguration
    ) {
        return this.modalService.open({
            pageComponent: PanelGlazingsListPageComponent,
            resolve: {
                fillings: () => panelGlazings,
                selectedFilling: () => selectedFilling,
            },
        }).result;
    }

    getPanelGlazingsByFieldType(field, conf, sash?) {
        sash = sash ? sash : this.getDoorSash(conf, field);
        let panelGlazings = [];
        let filling = {};
        if (typeof field === 'undefined' || field === 'default') {
            panelGlazings = this.getMatchingPanelGlazings(conf);
            sash = conf.Sashes[0];
            filling = conf.Sashes[0].glazing;
        } else if (field === 'doorActive') {
            panelGlazings = this.getMatchingPanelGlazingsForDoorSashes(conf, true);
            sash = conf.Sashes.filter(s => s.type.type === 'DRA' || s.type.type === 'DOA')[0];
            filling = sash.glazing;
        } else if (field === 'doorActiveInner') {
            panelGlazings = this.getMatchingInnerPanelsForDoorSashes(conf, true);
            sash = conf.Sashes.filter(s => s.type.type === 'DRA' || s.type.type === 'DOA')[0];
            filling = sash.panelInner;
        } else if (field === 'doorPassive') {
            panelGlazings = this.getMatchingPanelGlazingsForDoorSashes(conf, false);
            sash = conf.Sashes.filter(s => s.type.type === 'DRP' || s.type.type === 'DOP')[0];
            filling = sash.glazing;
        } else if (field === 'doorPassiveInner') {
            panelGlazings = this.getMatchingInnerPanelsForDoorSashes(conf, false);
            sash = conf.Sashes.filter(s => s.type.type === 'DRP' || s.type.type === 'DOP')[0];
            filling = sash.panelInner;
        } else if (field === 'door') {
            if (conf.Sashes.length === 0) {
                panelGlazings = this.getMatchingPanelGlazingsForModel(conf);
                sash = {
                    ...conf.ModelOptions,
                    glazing: conf.Model,
                };
                filling = conf.Model;
            } else {
                const doorType = ['DRA', 'DOA', 'DRP', 'DOP'];
                panelGlazings = this.getMatchingPanelGlazingsFor(
                    conf,
                    s => s.type && doorType.indexOf(s.type.type) > -1
                );
                sash = conf.Sashes.filter(s => s.type && doorType.indexOf(s.type.type) > -1)[0];
                filling = sash.glazing;
            }
        } else {
            panelGlazings = this.getMatchingPanelGlazingsForSash(field);
            filling = field.glazing;
        }

        return {
            panelGlazings,
            filling,
            sash,
        };
    }

    changePanelTrim(field, conf: DoorActiveConfiguration, sash?: Partial<ActiveSash>, face:  'outside' | 'inside' = 'outside') {
        const fillingsData = this.getPanelTrimsByFieldType(field, conf, sash, face);
        const panelTrims = fillingsData.panelTrims
        const trim = fillingsData.trim;
        sash = fillingsData.sash;

        if (field.includes('Inner') && conf.System.door_type) {
            const outerPanelTrims = this.getPanelTrimsByFieldType(field.replace('Inner', ''), conf)?.panelTrims;
            const areTheSame = panelTrims?.length === outerPanelTrims?.length && panelTrims?.every((v) => outerPanelTrims.includes(v));
            if (areTheSame) {
                return null;
            }
        }

        if (panelTrims.length === 0) {
            return null;
        } else {
            return this.openModalPanelTrims(panelTrims, trim, conf).then(
                result => {
                    if (result) {
                        if (conf.Sashes.length === 0) {
                            if (field === 'door') {
                                conf.ModelOptions.panelTrim = result.trim;
                            } else if (field === 'doorInner') {
                                conf.ModelOptions.innerPanelTrim = result.trim;
                                face = 'inside';
                            }
                        } else {
                            if (field === 'doorActive' && (face === 'outside' || conf.System.door_type)) {
                                conf.ModelOptions.panelTrim = result.trim;
                            }
                            if (field === 'doorActiveInner' || field === 'doorActive' && face === 'inside' && !conf.System.door_type) {
                                conf.ModelOptions.innerPanelTrim = result.trim;
                                face = 'inside';
                            }
                            if(field === 'doorPassive' && (face === 'outside' || conf.System.door_type)) {
                                conf.ModelOptions.passivePanelTrim = result.trim;
                            }
                            if(field === 'doorPassiveInner' || field === 'doorPassive' && face === 'inside' && !conf.System.door_type) {
                                conf.ModelOptions.passiveInnerPanelTrim = result.trim;
                                face = 'inside';
                            }
                            if (typeof field === 'undefined' || field === 'default') {
                                this.setPanelTrimsInAllSashes(conf, result.trim, face);
                            } else {
                                this.setPanelTrimInSash(sash, result.trim, conf, face);
                            }
                        }
                        return {
                            trim: conf.ModelOptions.panelTrim,
                            innerTrim: conf.ModelOptions.innerPanelTrim,
                        };
                    }
                }
            );
        }
    }

    openModalPanelTrims(
        panelTrims: IccDoorPanelTrim[],
        selectedTrim: Partial<IccDoorPanelTrim>,
        conf: WindowActiveConfiguration
    ) {
        return this.modalService.open({
            pageComponent: PanelTrimsListPageComponent,
            resolve: {
                doorPanelTrims: () => panelTrims,
                selectedTrim: () => selectedTrim,
            },
        }).result;
    }

    getPanelTrimsByFieldType(field, conf, sash?: Partial<ActiveSash>, face: 'outside' | 'inside' = 'outside') {
        sash = sash ? sash : this.getDoorSash(conf, field);
        let panelTrims = [];
        let trim: Partial<IccDoorPanelTrim> | null = null;
        if (typeof field === 'undefined' || field === 'default') {
            panelTrims = this.getMatchingPanelTrims(conf, face);
            sash = conf.Sashes[0];
            if (face === 'inside') {
                trim = conf.Sashes[0].innerPanelTrim;
            } else {
                trim = conf.Sashes[0].panelTrim;
            }
        } else if (field === 'doorActive' && (face === 'outside' || conf.System.door_type)) {
            panelTrims = this.getMatchingPanelTrimsForDoorSashes(conf, true);
            sash = conf.Sashes.filter(s => s.type.type === 'DRA' || s.type.type === 'DOA')[0];
            trim = sash.panelTrim;
        } else if (field === 'doorActiveInner' || field === 'doorActive' && face === 'inside' && !conf.System.door_type) {
            panelTrims = this.getMatchingPanelTrimsForDoorSashes(conf, true, 'inside');
            sash = conf.Sashes.filter(s => s.type.type === 'DRA' || s.type.type === 'DOA')[0];
            trim = sash.innerPanelTrim;
        } else if (field === 'doorPassive' && (face === 'outside' || conf.System.door_type)) {
            panelTrims = this.getMatchingPanelTrimsForDoorSashes(conf, false);
            sash = conf.Sashes.filter(s => s.type.type === 'DRP' || s.type.type === 'DOP')[0];
            trim = sash.panelTrim;
        } else if (field === 'doorPassiveInner' || field === 'doorPassive' && face === 'inside' && !conf.System.door_type) {
            panelTrims = this.getMatchingPanelTrimsForDoorSashes(conf, false, 'inside');
            sash = conf.Sashes.filter(s => s.type.type === 'DRP' || s.type.type === 'DOP')[0];
            trim = sash.innerPanelTrim;
        } else if (field === 'door') {
            if (conf.Sashes.length === 0) {
                panelTrims = this.getMatchingPanelTrimsForModel(conf, face);
                sash = {
                    ...conf.ModelOptions,
                    glazing: conf.Model,
                };
                if (face === 'inside') {
                    trim = conf.ModelOptions.innerPanelTrim;
                } else {
                    trim = conf.ModelOptions.panelTrim;
                }
            } else {
                const doorType = ['DRA', 'DOA', 'DRP', 'DOP'];
                panelTrims = this.getMatchingPanelTrimsFor(
                    conf,
                    s => s.type && doorType.indexOf(s.type.type) > -1,
                    face,
                );
                sash = conf.Sashes.filter(s => s.type && doorType.indexOf(s.type.type) > -1)[0];
                if (face === 'inside') {
                    trim = sash.innerPanelTrim;
                } else {
                    trim = sash.panelTrim;
                }
            }
        } else {
            panelTrims = this.getMatchingPanelTrimsForSash(field, conf, face);
            if (face === 'inside') {
                trim = sash.innerPanelTrim;
            } else {
                trim = sash.panelTrim;
            }
        }

        return {
            panelTrims,
            trim,
            sash,
        };
    }

    getMatchingFillings(conf: WindowActiveConfiguration) {
        return this.getMatchingFillingsFor(conf, () => true);
    }

    validateFillingColors(conf: WindowActiveConfiguration, sash: ActiveSash | 'activeDoorSash') {
        this.newColorsService.validateElementColor(conf, {
            element: 'filling',
            sash,
            side: 'outer',
        });
        if((sash instanceof ActiveSash && sash.glazing.available_second_color) || sash === 'activeDoorSash')  {
            this.newColorsService.validateElementColor(conf, {
                element: 'filling',
                sash,
                side: 'outer',
                alt: true,
            });
        } else {
            this.newColorsService.clearElementColor(conf, {
                element: 'filling',
                sash,
                side: 'outer',
                alt: true,
            });
        }

        if (sash instanceof ActiveSash && sash.panelInner) {
            this.newColorsService.validateElementColor(conf, {
                element: 'filling',
                sash,
                side: 'inner',
            });
            if (sash.panelInner.available_second_inside_color) {
                this.newColorsService.validateElementColor(conf, {
                    element: 'filling',
                    sash,
                    side: 'inner',
                    alt: true,
                });
            } else {
                this.newColorsService.clearElementColor(conf, {
                    element: 'filling',
                    sash,
                    side: 'inner',
                    alt: true,
                });
            }
        }

        this.checkSingleFillingColors(conf, null);
    }

    getDoorSash(conf: WindowActiveConfiguration | DoorActiveConfiguration, boxType?: string) {
        let sash: ActiveSash;
        if (conf.Sashes) {
            if (typeof boxType === 'undefined' || boxType === 'default') {
                sash = conf.Sashes[0];
            } else if (boxType === 'doorActive' || boxType === 'doorActiveInner') {
                sash = conf.Sashes.find(s => s.type.type === 'DRA' || s.type.type === 'DOA');
            } else if (boxType === 'doorPassive') {
                sash = conf.Sashes.find(s => s.type.type === 'DRP' || s.type.type === 'DOP');
            } else if (boxType === 'door') {
                sash = conf.Sashes.find(
                    s =>
                        s.type.type === 'DRA'
                        || s.type.type === 'DRP'
                        || s.type.type === 'DOA'
                        || s.type.type === 'DOP'
                );
            } else if (boxType === 'window') {
                sash = conf.Sashes.find(
                    s =>
                        s.type.type !== 'DRA'
                        && s.type.type !== 'DRP'
                        && s.type.type !== 'DOA'
                        && s.type.type !== 'DOP'
                );
            } else if (boxType === 'topLight') {
                sash = conf.Sashes.find(s => {
                    const frame = conf.Frames.find(f => f.id === s.frameId);
                    return (
                        s.type.type === 'F'
                        && s.nearMullions.left === -1
                        && ((frame && frame.x) || 0) + s.rx === 0
                        && s.nearMullions.top === -1
                        && ((frame && frame.x) || 0) + s.rx + s.rWidth === conf.Width
                    );
                });
            } else if (boxType === 'leftLight') {
                sash = conf.Sashes.find(s => {
                    const frame = conf.Frames.find(f => f.id === s.frameId);
                    return (
                        s.type.type === 'F'
                        && s.nearMullions.left === -1
                        && ((frame && frame.x) || 0) + s.rx === 0
                        && (s.nearMullions.top !== -1
                            || ((frame && frame.x) || 0) + s.rx + s.rWidth !== conf.Width)
                    );
                });
            } else if (boxType === 'rightLight') {
                sash = conf.Sashes.find(s => {
                    const frame = conf.Frames.find(f => f.id === s.frameId);
                    return (
                        s.type.type === 'F'
                        && s.nearMullions.right === -1
                        && ((frame && frame.x) || 0) + s.rx > 0
                    );
                });
            }
        }
        return sash;
    }

    getFilterForField(field, conf: WindowActiveConfiguration) {
        let sashFilter = sash => true;
        if (field === 'window') {
            sashFilter = sash => ['DRA', 'DRP', 'DOA', 'DRP'].indexOf(sash.type.type) === -1;
        } else if (field === 'doorActive' || field === 'doorActiveInner') {
            sashFilter = sash => ['DRA', 'DOA'].indexOf(sash.type.type) > -1;
        } else if (field === 'doorPassive'|| field === 'doorPassiveInner') {
            sashFilter = sash => ['DRP', 'DOP'].indexOf(sash.type.type) > -1;
        } else if (field === 'door') {
            sashFilter = sash => ['DRA', 'DOA', 'DRP', 'DOP'].indexOf(sash.type.type) > -1;
        } else if (field === 'doorLight') {
            sashFilter = sash => sash.type.type === 'F';
        } else if (field === 'doorTopLight') {
            sashFilter = sash => {
                const frame = conf.Frames.find(f => f.id === sash.frameId);
                return (
                    sash.type.type === 'F'
                    && sash.nearMullions.left === -1
                    && ((frame && frame.x) || 0) + sash.rx === 0
                    && sash.nearMullions.top === -1
                    && ((frame && frame.x) || 0) + sash.rx + sash.rWidth === conf.Width
                );
            };
        } else if (field === 'doorLeftLight') {
            sashFilter = sash => {
                const frame = conf.Frames.find(f => f.id === sash.frameId);
                return (
                    sash.type.type === 'F'
                    && sash.nearMullions.left === -1
                    && ((frame && frame.x) || 0) + sash.rx === 0
                    && (sash.nearMullions.top !== -1
                        || ((frame && frame.x) || 0) + sash.rx + sash.rWidth !== conf.Width)
                );
            };
        } else if (field === 'doorRightLight') {
            sashFilter = sash => {
                const frame = conf.Frames.find(f => f.id === sash.frameId);
                return (
                    sash.type.type === 'F'
                    && sash.nearMullions.right === -1
                    && ((frame && frame.x) || 0) + sash.rx > 0
                );
            };
        }

        return sashFilter;
    }

    private getMatchingFillingsForWindowSashes(conf: WindowActiveConfiguration) {
        return this.getMatchingFillingsFor(conf, this.getFilterForField('window', conf));
    }

    getMatchingFillingsForDoorSashes(
        conf: WindowActiveConfiguration,
        type:
            | 'doorActive'
            | 'doorActiveInner'
            | 'doorPassive'
            | 'doorPassiveInner'
            | 'door'
            | 'doorLight'
            | 'doorTopLight'
            | 'doorLeftLight'
            | 'doorRightLight' = 'door',
        noValidation = false
    ) {
        return this.getMatchingFillingsFor(conf, this.getFilterForField(type, conf), noValidation);
    }

    getMatchingFillingVariantsForField(
        conf: WindowActiveConfiguration,
        field: string | ActiveSash,
        sash?: ActiveSash,
    ) {
        let matchingFillings = [];
        if (typeof field === 'string') {
            matchingFillings = this.getMatchingFillingsFor(conf, this.getFilterForField(field, conf))
        } else {
            matchingFillings = this.getMatchingFillingsInSash(field, sash, conf);
        }
        matchingFillings = matchingFillings.filter(
            el => el.id.split('.')[0] === sash.glazing.id.split('.')[0]
        );

        matchingFillings = Object.values(matchingFillings.reduce((acu, filling) => {
            if (!acu[filling.panel_type] || Number(acu[filling.panel_type].thickness_mm) < Number(filling.thickness_mm)) {
                acu[filling.panel_type] = filling;
            }
            return acu;
        }, {}));

        return matchingFillings;
    }

    checkGlazing(glazing, glazingRestricts, width = 0, height = 0, area: any = 0) {
        if (!isArray(glazingRestricts)) {
            return true
        }
        const restricts = glazingRestricts.filter(glazingRestrict =>
            parseInt(glazing.thinkness_glass) === parseInt(glazingRestrict.thickness)
            && parseInt(glazing.frame_width) >= parseInt(glazingRestrict.frame_width)
            );
        if (restricts.length > 0) {
            return restricts.some(restrict => {
                let ratio = restrict.ratio.split(':');
                ratio = ratio[1] / ratio[0];
                const areaInCorrect = parseFloat(area) > parseFloat(restrict.area);
                const lengthMinInCorrect = (width < restrict.length_min) || (height < restrict.length_min);
                const lengthMaxInCorrect = (width > restrict.length_max) || (height > restrict.length_max);
                const rationInCorrect = (width >= height && width / height > ratio) || (height / width > ratio);
                return !(areaInCorrect || lengthMinInCorrect || lengthMaxInCorrect || rationInCorrect);
            })
        }
        return true;
    }

    updateStepLights(conf: WindowActiveConfiguration) {
        if (this.getMatchingFillingsForDoorSashes(conf, 'doorLight').length <= 1) {
            this.stepsService.disable('lightsglazing');
        } else {
            this.stepsService.enable('lightsglazing');
        }
    }

    private getMatchingFillingsFor(conf: WindowActiveConfiguration, filter: (sash) => boolean, noValidation = false) {

        let matchingFillings = this.getMatchingFillingsGeneral(conf, noValidation);
        (conf.Sashes || []).filter(filter).forEach(sash => {
            sash.intSashes.forEach(intSash => {
                matchingFillings = this.getCommonFillingsInSash(
                    matchingFillings,
                    intSash,
                    sash,
                    conf,
                    noValidation
                );
            });
            if (sash.intSashes.length === 0) {
                matchingFillings = this.getCommonFillingsInSash(matchingFillings, sash, sash, conf, noValidation);
            }
        });
        return matchingFillings;
    }

    private getMatchingFillingsInSash(
        field,
        sash,
        conf: WindowActiveConfiguration | DoorActiveConfiguration,
        noValidation = false
    ) {
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
        ) {
            const matchingThickness = this.glazingBeadsService.getMatchingFillingThicknessInSash(
                field,
                sash,
                conf
            );

            const matchingThicknessPanelDouble = this.glazingBeadsService.getMatchingFillingThicknessInSash(
                field,
                sash,
                conf,
                'panel_type_double'
            );

            const matchingThicknessPanelInner = this.glazingBeadsService.getMatchingFillingThicknessInSash(
                field,
                sash,
                conf,
                'panel_type_inner'
            );

            const matchingThicknessPanelOuter = this.glazingBeadsService.getMatchingFillingThicknessInSash(
                field,
                sash,
                conf,
                'panel_type_outer'
            );

            const matchingFillings = this.fillings.filter(
                filling =>
                    (sash.type
                        && sash.type.type !== 'F'
                        && ((this.isDoorPanel(filling)
                            && (filling.type === 'deco_panels'
                                && this.isDecoPanelMatchToDimensions(filling, field, sash, conf)
                                && this.isDecoPanelMatchToDepth(filling, {
                                    panel_type_inset: matchingThickness,
                                    panel_type_double: matchingThicknessPanelDouble,
                                    panel_type_inner: matchingThicknessPanelInner,
                                    panel_type_outer: matchingThicknessPanelOuter,
                                })
                            ))
                            || (filling.type === 'door_panels'
                                && this.isDoorPanelMatchToDoorSizes(
                                    filling,
                                    sash,
                                    conf as DoorActiveConfiguration
                                ))))
                    || (!this.isDoorPanel(filling)
                        && filling.type !== 'panels_glazing'
                        && matchingThickness.some(
                            range =>
                                Number(filling.thickness_mm) >= range.min
                                && Number(filling.thickness_mm) <= range.max
                        )
                        && this.isFillingMatchToSashType(conf, sash, filling))
            );

            if (!noValidation) {
                this.issuesService.unregister(
                    `no-matching-fillings-in-sash-${field.id}-${sash.id}`,
                    conf
                );
                if (matchingFillings.length === 0) {
                    this.issuesService.registerDataProblem(
                        `no-matching-fillings-in-sash-${field.id}-${sash.id}`,
                        'Brak pasujących wypełnień',
                        conf,
                        {
                            level: IssueLevel.FATAL,
                            extra: {
                                matchingThickness: core.stringJson(matchingThickness),
                                sashType: sash.type.type,
                                systemId: conf.System.id,
                                systemName: conf.System.name,
                                systemType: conf.System.type,
                            },
                        }
                    );
                }
            }
            return matchingFillings;
        }
        return [];
    }

    private doorPanelPassiveMatchesActive(
        filling: IccFilling,
        conf: DoorActiveConfiguration
    ) {
        const selectedActiveSashDoorPanel = conf.Model;
        const fillingId = filling.id && String(filling.id).split('.')[0]
        if (
            selectedActiveSashDoorPanel
            && Array.isArray(selectedActiveSashDoorPanel.door_panel_passive_ids)
            && selectedActiveSashDoorPanel.door_panel_passive_ids.map(Number).includes(Number(fillingId))
        ) {
            return true;
        }

        return false;
    }

    private getMatchingFillingsGeneral(conf: WindowActiveConfiguration | DoorActiveConfiguration, noValidation = false) {
        if (this.validationService.isValidElements(conf, ['loadedFillings'])) {
            const matchingFillings = this.fillings.filter(
                filling =>
                    this.isDoorPanel(filling)
                    || (!this.isDoorPanel(filling)
                        && filling.type !== 'panels_glazing'
                        && this.isFillingMatchToSashTypeGeneral(conf, filling)
                    )
            );

            if (!noValidation) {
                this.issuesService.unregister(`no-matching-fillings-in-general`, conf);
                if (matchingFillings.length === 0) {
                    this.issuesService.registerDataProblem(
                        `no-matching-fillings-in-general`,
                        'Brak pasujących wypełnień',
                        conf,
                        {
                            level: IssueLevel.FATAL,
                            extra: {
                                systemId: conf.System.id,
                                systemName: conf.System.name,
                                systemType: conf.System.type,
                            },
                        }
                    );
                }
            }
            return matchingFillings;
        }
        return [];
    }

    private isFillingMatchToSashTypeGeneral(conf: WindowActiveConfiguration, filling: IccFilling): any {
        const notDoor = conf.type !== 'door'
            || ((conf as DoorActiveConfiguration).Model
            && (conf as DoorActiveConfiguration).Model.standard_fills)
        const notDoorSash = (sash) => ['DRA', 'DRP', 'DOA', 'DOP'].indexOf(sash.type.type) === -1;
        const isDoorLight = (sash) => conf.type === 'door' && notDoorSash(sash);
        const isSideLight = (sash) => {
            const frame = conf.Frames.find(f => f.id === sash.frameId);
            return isDoorLight(sash) && frame.y + sash.ry + sash.rHeight === conf.Height
        };
        const isTopLight = (sash) => {
            const frame = conf.Frames.find(f => f.id === sash.frameId);
            return isDoorLight(sash) && frame.y + sash.ry + sash.rHeight < conf.Height;
        };
        return (
            (
                notDoor
                && conf.Sashes.some(sash => notDoorSash(sash))
                && filling.conf.indexOf('window') > -1
            ) || conf.Sashes.some(sash => isSideLight(sash)) && filling.conf.indexOf('side-light') > -1
            || conf.Sashes.some(sash => isTopLight(sash)) && filling.conf.indexOf('top-light') > -1
            || (conf.Sashes.length === 0 && filling.conf.indexOf('window') > -1)
            || filling.conf.indexOf('door') > -1
        );
    }

    private isDecoPanelMatchToDimensions(filling: IccFilling, field: any, sash: any, conf: WindowActiveConfiguration) {
        const panelDimensions = this.getDecoPanelSpaceDimensions(field, sash, filling, conf);
        const panelWidthMin =
            filling.panel_width_min || this.decoPanelRestrictiveDimensions.minWidth;
        const panelHeightMin =
            filling.panel_height_min || this.decoPanelRestrictiveDimensions.minHeight;
        const panelWidthMax =
            filling.panel_width_max || this.decoPanelRestrictiveDimensions.maxWidth;
        const panelHeightMax =
            filling.panel_height_max || this.decoPanelRestrictiveDimensions.maxHeight;
        return (
            Number(panelWidthMin) <= panelDimensions.width
            && Number(panelWidthMax) >= panelDimensions.width
            && Number(panelHeightMin) <= panelDimensions.height
            && Number(panelHeightMax) >= panelDimensions.height
        );
    }

    private isDecoPanelMatchToDepth(filling: IccFilling, matchingThicknesses: Record<IccFilling['panel_type'], {
        min: number;
        max: number;
    }[]>) {
        const overlayThickness = this.getDecoPanelOverlayThickness(filling);
        return this.isAlwaysMatchProfile(filling) || matchingThicknesses[filling.panel_type].some(
            range =>
                Number(filling.thickness_mm) - overlayThickness >= range.min
                && Number(filling.thickness_mm) - overlayThickness <= range.max
        )
    }

    private isAlwaysMatchProfile(filling: IccFilling) {
        return filling.type === 'deco_panels'
            && (filling.panel_type === 'panel_type_double' || filling.panel_type === 'panel_type_inner')
            && Boolean(filling.always_match_profile);
    }

    private isDoorPanelMatchToDoorSizes(
        filling: IccFilling,
        sash: ActiveSash | null,
        conf: DoorActiveConfiguration,
        inner = false
    ) {
        if(!sash?.type?.type) {
            return true;
        }
        if (sash && ['DRP', 'DOP'].indexOf(sash.type.type) > -1) {
            const fillingMatchToPassiveDoorSize = sash && this.fillingSizeMatchToDoorSize(filling, sash, conf, inner);
            return (
                filling.door_sizes_ids
                && filling.door_sizes_ids.map(Number).includes(conf.doorSizes.passiveSashSizeId)
                && (filling.max_shortening == null
                    || conf.doorSizes.shortening <= Number(filling.max_shortening))
                && this.doorPanelPassiveMatchesActive(filling, conf)
                && fillingMatchToPassiveDoorSize
            );
        }
        if (!sash || ['DRA', 'DOA'].indexOf(sash.type.type) > -1) {
            const fillingMatchToDoorSize = sash && this.fillingSizeMatchToDoorSize(filling, sash, conf, inner);
            return (
                filling.door_sizes_ids
                && filling.door_sizes_ids.map(Number).includes(conf.doorSizes.sashSizeId)
                && (filling.max_shortening == null
                    || conf.doorSizes.shortening <= Number(filling.max_shortening))
                && (fillingMatchToDoorSize || !sash)
            );
        }
        return false;
    }

    private fillingSizeMatchToDoorSize(
        filling: IccFilling,
        sash: ActiveSash | null,
        conf: DoorActiveConfiguration,
        inner = false) {
            const doorDataSashesSizeSash = filling.size_limit_type === 'sash_with_frame'
                ? this.getDoorDataSashesWithFrameSize(sash, conf)
                : this.getDoorDataSashesSize(sash, conf);
            const side = inner ? 'inner' : 'outer';

            return (!Number(filling.panel_width_max) || Number(filling.panel_width_max) >= doorDataSashesSizeSash[side].width)
                && (!Number(filling.panel_width_min) || Number(filling.panel_width_min) <= doorDataSashesSizeSash[side].width)
                && (!Number(filling.panel_height_max) || Number(filling.panel_height_max) >= doorDataSashesSizeSash[side].height)
                && (!Number(filling.panel_height_min) || Number(filling.panel_height_min) <= doorDataSashesSizeSash[side].height);
    }

    private getDoorDataSashesSize(
        sash: ActiveSash | null,
        conf: WindowActiveConfiguration | DoorActiveConfiguration) {

            const heightWithRebate = sash ? this.getSashHeightBasedOnHeight(conf) : Number.POSITIVE_INFINITY;
            const widthWithRebate = sash ? this.getSashWidthBasedOnWidth(conf) : Number.POSITIVE_INFINITY;

            const heightWithoutRebate = sash ? this.getSashHeightBasedOnHeight(conf, false) : Number.POSITIVE_INFINITY;
            const widthWithoutRebate = sash ? this.getSashWidthBasedOnWidth(conf, false) : Number.POSITIVE_INFINITY;
            const typeTypeDRA = sash?.type?.type === 'DRA';

            return {
                inner: {
                    height: typeTypeDRA ? heightWithRebate : heightWithoutRebate,
                    width: typeTypeDRA ? widthWithRebate : widthWithoutRebate
                },
                outer: {
                    height: typeTypeDRA ? heightWithoutRebate : heightWithRebate,
                    width: typeTypeDRA ? widthWithoutRebate : widthWithRebate
                }
            }
    }

    private getDoorDataSashesWithFrameSize(
        sash: ActiveSash | null,
        conf: WindowActiveConfiguration | DoorActiveConfiguration) {

            const height = conf.doorSizes.isStandardHeightSelected || conf.doorSizes.customHeight === 0
                ? sash ? sash.rHeight : Number.POSITIVE_INFINITY
                : conf.doorSizes.customHeight
            const width = conf.doorSizes.isStandardHeightSelected || conf.doorSizes.customWidth === 0
                ? sash ? sash.rWidth : Number.POSITIVE_INFINITY
                : conf.doorSizes.customWidth;

            return {
                inner: { height, width },
                outer: { height, width }
            }
    }

    private getDecoPanelSpaceDimensions(field, sash, filling, conf: WindowActiveConfiguration) {
        const sashDrawData =
            conf.drawData
            && conf.drawData.sash
            && conf.drawData.sash.find(s => s.sashId === field.id);
        const fillingDrawData =
            conf.drawData
            && conf.drawData.filling
            && conf.drawData.filling.find(s => s.sashId === field.id);
        let panelWidth = field.rWidth;
        let panelHeight = field.rHeight;
        if (sashDrawData) {
            panelWidth = sashDrawData.inner.rect.width;
            panelHeight = sashDrawData.inner.rect.height;

            if (filling.panel_type === 'panel_type_inset' && fillingDrawData?.insetData) {
                panelWidth = fillingDrawData.insetData.rect.width;
                panelHeight = fillingDrawData.insetData.rect.height;
            }
            if (
                filling.panel_type === 'panel_type_outer'
                || filling.panel_type === 'panel_type_double'
            ) {
                panelWidth = sashDrawData.outer.rect.width;
                panelHeight = sashDrawData.outer.rect.height;
            }
        }
        return {
            width: panelWidth,
            height: panelHeight,
        };
    }

    private getMatchingPanelGlazings(conf: WindowActiveConfiguration) {
        return this.getMatchingPanelGlazingsFor(
            conf,
            sash => ['DRA', 'DRP', 'DOA', 'DOP'].indexOf(sash.type.type) > -1
        );
    }

    private getMatchingPanelGlazingsForDoorSashes(conf: WindowActiveConfiguration, active = true) {
        const doorType = active ? ['DRA', 'DOA'] : ['DRP', 'DOP'];
        return this.getMatchingPanelGlazingsFor(
            conf,
            sash => doorType.indexOf(sash.type.type) > -1
        );
    }

    private getMatchingPanelGlazingsFor(
        conf: WindowActiveConfiguration,
        filter: (sash) => boolean
    ) {
        let matchingFillings = [];
            conf.Sashes.filter(filter).forEach(sash => {
                sash.intSashes.forEach(intSash => {
                    matchingFillings = this.getCommonPanelGlazingsInSash(matchingFillings, intSash);
                });
                if (sash.intSashes.length === 0) {
                    matchingFillings = this.getCommonPanelGlazingsInSash(matchingFillings, sash);
                }
            });
        return matchingFillings;
    }

    private getMatchingPanelTrimsFor(
        conf: WindowActiveConfiguration,
        filter: (sash) => boolean,
        face: 'outside' | 'inside' = 'outside'
    ) {
        let matchingTrims = [];
        conf.Sashes.filter(filter).forEach(sash => {
            sash.intSashes.forEach(intSash => {
                matchingTrims = this.getCommonPanelTrimsInSash(matchingTrims, intSash, face);
            });
            if (sash.intSashes.length === 0) {
                matchingTrims = this.getCommonPanelTrimsInSash(matchingTrims, sash, face);
            }
        });
        return matchingTrims;
    }

    private getMatchingPanelTrims(conf: WindowActiveConfiguration, face: 'outside' | 'inside' = 'outside') {
        return this.getMatchingPanelTrimsFor(
            conf,
            sash => ['DRA', 'DRP', 'DOA', 'DOP'].indexOf(sash.type.type) > -1,
            face,
        );
    }

    private getMatchingPanelTrimsForDoorSashes(conf: WindowActiveConfiguration, active = true, face: 'outside' | 'inside' = 'outside') {
        const doorType = active ? ['DRA', 'DOA'] : ['DRP', 'DOP'];
        return this.getMatchingPanelTrimsFor(
            conf,
            sash => doorType.indexOf(sash.type.type) > -1,
            face,
        );
    }


    getMatchingInnerPanelsForDoorSashes(conf: DoorActiveConfiguration, active = true) {
        const doorType = active ? ['DRA', 'DOA'] : ['DRP', 'DOP'];
        return this.getMatchingInnerPanelsFor(conf, sash => doorType.indexOf(sash.type.type) > -1);
    }

    getMatchingInnerPanelsForDoorSashesFiltered(conf: DoorActiveConfiguration, active = true) {
        const doorType = active ? ['DRA', 'DOA'] : ['DRP', 'DOP'];
        return this.getMatchingInnerPanelsFor(
            conf,
            sash => doorType.indexOf(sash.type.type) > -1
        ).filter(f => f.type === (conf.System.door_type ? 'door_panels' : 'deco_panels'));
    }

    private getMatchingInnerPanelsFor(conf: DoorActiveConfiguration, filter: (sash) => boolean) {
        let matchingFillings = [];
        conf.Sashes.filter(filter).forEach(sash => {
            sash.intSashes.forEach(intSash => {
                matchingFillings = this.getCommonInnerPanelsInSash(
                    matchingFillings,
                    intSash,
                    sash,
                    conf
                );
            });
            if (sash.intSashes.length === 0) {
                matchingFillings = this.getCommonInnerPanelsInSash(
                    matchingFillings,
                    sash,
                    sash,
                    conf
                );
            }
        });
        return matchingFillings;
    }

    private getMatchingPanelGlazingsForSash(field, conf = this.configurationsService.conf.Current) {
        return this.fillings.filter(
            filling =>
                filling.type === 'panels_glazing'
                && field.glazing
                && field.glazing.panel_glazings_ids
                && field.glazing.panel_glazings_ids.map(Number).indexOf(parseInt(filling.id)) > -1
        );
    }
    private getMatchingPanelTrimsForSash(field: ActiveSash, conf = this.configurationsService.conf.Current, face: 'outside' | 'inside' = 'outside') {
        const filling = face === 'inside' && field.glazing?.type !== 'deco_panels' ? field.panelInner : field.glazing;
        return this.doorPanelTrims.filter(
            trim =>
                filling
                && (face === 'inside' && filling.doorPanelTrimsInside?.length
                    && filling.doorPanelTrimsInside.map(Number).indexOf(Number(trim.id)) > -1
                    || face === 'outside'
                    && filling.doorPanelTrimsOutside?.length
                    && filling.doorPanelTrimsOutside.map(Number).indexOf(Number(trim.id)) > -1
                ),
        );
    }

    private getMatchingPanelGlazingsForModel(conf: DoorActiveConfiguration) {
        return this.fillings.filter(
            filling =>
                filling.type === 'panels_glazing'
                && conf.Model
                && conf.Model.panel_glazings_ids
                && conf.Model.panel_glazings_ids.map(Number).indexOf(parseInt(filling.id)) > -1
                && conf.Model.panel_glazings_ids.find(k =>
                    this.fillings.some(f => Number(f.id) == k)
                )
        );
    }
    private getMatchingPanelTrimsForModel(conf: DoorActiveConfiguration, face: 'outside' | 'inside' = 'outside') {
        const model = face === 'inside' && conf.Model?.type !== 'deco_panels' ? conf.ModelOptions?.panelInner : conf.Model;
        return this.doorPanelTrims.filter(
            trim =>
                model
                && (face === 'inside'
                    && model.doorPanelTrimsInside?.length
                    && model.doorPanelTrimsInside.map(Number).indexOf(Number(trim.id)) > -1
                    || face === 'outside'
                        && model.doorPanelTrimsOutside?.length
                        && model.doorPanelTrimsOutside.map(Number).indexOf(Number(trim.id)) > -1
            ),
        );
    }

    private getMatchingPassivePanelTrimsForModel(conf: DoorActiveConfiguration, face: 'outside' | 'inside' = 'outside') {
        const model = face === 'inside' ? conf.ModelOptions?.passiveInnerSashModel : conf.ModelOptions?.passiveSashModel;
        return this.doorPanelTrims.filter(
            trim =>
                model
                && (face === 'inside'
                    && model.doorPanelTrimsInside?.length
                    && model.doorPanelTrimsInside.map(Number).indexOf(Number(trim.id)) > -1
                    || face === 'outside'
                        && model.doorPanelTrimsOutside?.length
                        && model.doorPanelTrimsOutside.map(Number).indexOf(Number(trim.id)) > -1
            ),
        );
    }

    private getMatchingPassivePanelGlazingsForModel(conf: DoorActiveConfiguration) {
        return this.fillings.filter(
            filling =>
                filling.type === 'panels_glazing'
                && conf.ModelOptions
                && conf.ModelOptions.passiveSashModel
                && conf.ModelOptions.passiveSashModel.panel_glazings_ids
                && conf.ModelOptions.passiveSashModel.panel_glazings_ids.map(Number).indexOf(parseInt(filling.id)) > -1
                && conf.ModelOptions.passiveSashModel.panel_glazings_ids.find(k =>
                    this.fillings.some(f => Number(f.id) === Number(k))
                )
        );
    }

    private getMatchingInnerPanelsForSash(
        field,
        sash,
        conf: WindowActiveConfiguration | DoorActiveConfiguration
    ) {
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
            && (field.glazing
                && field.glazing.type === 'door_panels'
                && field.glazing.is_double_sided
                && field.glazing.is_double_sided.length > 0)
        ) {
            const matchingFillings = this.fillings.filter(
                filling =>
                    filling.type === 'door_panels'
                    && ((filling.is_flat && field.glazing.is_double_sided.includes('single'))
                        || (field.glazing.is_flat
                            && (filling.is_double_sided.includes('single') || filling.is_flat))
                        || (field.glazing.is_double_sided.includes('double')
                            && filling.id === field.glazing.id)
                        || (field.glazing.door_panel_ids
                            && (field.glazing.is_double_sided.includes('mixed')
                                && field.glazing.door_panel_ids
                                    .map(Number)
                                    .includes(parseInt(filling.id)))))
                    && this.isDoorPanelMatchToDoorSizes(
                        filling,
                        sash,
                        conf as DoorActiveConfiguration,
                        true
                    )
            );

            this.issuesService.unregister(
                `no-matching-fillings-in-sash-${field.id}-${sash.id}`,
                conf
            );
            if (matchingFillings.length === 0) {
                this.issuesService.registerDataProblem(
                    `no-matching-fillings-in-sash-${field.id}-${sash.id}`,
                    'Brak pasujących wypełnień',
                    conf,
                    {
                        level: IssueLevel.FATAL,
                        extra: {
                            sashType: sash.type.type,
                            systemId: conf.System.id,
                            systemName: conf.System.name,
                            systemType: conf.System.type,
                        },
                    }
                );
            }
            return matchingFillings;
        }
        return [];
    }

    private getMatchingInnerPanelsForModel(conf: DoorActiveConfiguration, noValidation = false) {
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
            && (conf.Model
                && conf.Model.type === 'door_panels'
                && conf.Model.is_double_sided
                && conf.Model.is_double_sided.length > 0)
        ) {
            const matchingFillings = this.fillings.filter(
                filling =>
                    filling.type === 'door_panels'
                    && ((filling.is_flat && conf.Model.is_double_sided.includes('single'))
                        || (conf.Model.is_flat && filling.is_double_sided.includes('single'))
                        || (conf.Model.is_double_sided.includes('double')
                            && filling.id === conf.Model.id)
                        || (conf.Model.door_panel_ids
                            && (conf.Model.is_double_sided.includes('mixed')
                                && conf.Model.door_panel_ids
                                    .map(Number)
                                    .includes(parseInt(filling.id)))))
                    && this.isDoorPanelMatchToDoorSizes(
                        filling,
                        null,
                        conf as DoorActiveConfiguration
                    )
            );

            if (!noValidation) {
                this.issuesService.unregister(`no-matching-fillings-in-model`, conf);
                if (matchingFillings.length === 0) {
                    this.issuesService.registerDataProblem(
                        `no-matching-fillings-in-model`,
                        'Brak pasujących wypełnień',
                        conf,
                        {
                            level: IssueLevel.FATAL,
                            extra: {
                                systemId: conf.System.id,
                                systemName: conf.System.name,
                                systemType: conf.System.type,
                            },
                        }
                    );
                }
            }
            return matchingFillings;
        }
        return [];
    }

    private getMatchingPassiveInnerPanelsForModel(conf: DoorActiveConfiguration, noValidation = false) {
        if (
            this.validationService.isValidElements(conf, [
                'frameProfiles',
                'sashesProfiles',
                'loadedFillings',
                'loadedGlazingBeads',
                'sashes',
            ])
            && (conf.ModelOptions
                && conf.ModelOptions.passiveSashModel
                && conf.ModelOptions.passiveSashModel.type === 'door_panels'
                && conf.ModelOptions.passiveSashModel.is_double_sided
                && conf.ModelOptions.passiveSashModel.is_double_sided.length > 0)
        ) {
            const sash = conf.Sashes.find(s => s.type.type === 'DRP' || s.type.type === 'DOP');
            const matchingFillings = this.fillings.filter(
                filling =>
                    filling.type === 'door_panels'
                    && ((filling.is_flat && conf.ModelOptions.passiveSashModel.is_double_sided.includes('single'))
                        || (conf.ModelOptions.passiveSashModel.is_flat && filling.is_double_sided.includes('single'))
                        || (conf.ModelOptions.passiveSashModel.is_double_sided.includes('double')
                            && filling.id === conf.ModelOptions.passiveSashModel.id)
                        || (conf.ModelOptions.passiveSashModel.door_panel_ids
                            && (conf.ModelOptions.passiveSashModel.is_double_sided.includes('mixed')
                                && conf.ModelOptions.passiveSashModel.door_panel_ids
                                    .map(Number)
                                    .includes(parseInt(filling.id)))))
                    && this.isDoorPanelMatchToDoorSizes(
                        filling,
                        sash,
                        conf as DoorActiveConfiguration
                    )
            );

            if (!noValidation) {
                this.issuesService.unregister(`no-matching-fillings-in-model-passive`, conf);
                if (matchingFillings.length === 0) {
                    this.issuesService.registerDataProblem(
                        `no-matching-fillings-in-model-passive`,
                        'Brak pasujących wypełnień do drzwi pasywnych',
                        conf,
                        {
                            level: IssueLevel.FATAL,
                            extra: {
                                systemId: conf.System.id,
                                systemName: conf.System.name,
                                systemType: conf.System.type,
                            },
                        }
                    );
                }
            }
            return matchingFillings;
        }
        return [];
    }

    private getMatchingPanelTypesForSash(sash: ActiveSash) {
        if (sash.glazing.type === 'door_panels') {
            const panelTypes = {
                Double: 'double',
                Outer: 'single',
                Inner: 'single',
                Mixed: 'mixed',
            };
            return Object.keys(panelTypes).reduce((prev, type) => {
                if (
                    sash.glazing.is_double_sided
                    && sash.glazing.is_double_sided.includes(panelTypes[type])
                ) {
                    prev.push(type);
                }
                return prev;
            }, []);
        } else {
            const panelTypes = {
                Inset: 'panel_type_inset',
                Outer: 'panel_type_outer',
                Double: 'panel_type_double',
                Inner: 'panel_type_inner',
            };
            return Object.keys(panelTypes).reduce((prev, type) => {
                if (sash.glazing.panel_type === panelTypes[type]) {
                    prev.push(type);
                }
                return prev;
            }, []);
        }
    }

    private getMatchingPanelType(matchingPanelTypes) {
        return matchingPanelTypes[0];
    }

    private getMatchingPanelTypeForSash(sash: ActiveSash, matchingPanelTypes: any[]) {
        if (
            !sash.glazing
            || !sash.glazing.id
            || sash.glazing.type !== 'door_panels'
            || !sash.panelInner
            || !sash.panelInner.id
        ) {
            return matchingPanelTypes[0];
        }
        if (sash.glazing.is_flat) {
            return 'Inner';
        } else if (sash.panelInner.is_flat) {
            return 'Outer';
        } else if (parseInt(sash.glazing.id) === parseInt(sash.panelInner.id)) {
            return 'Double';
        } else {
            return 'Mixed';
        }
    }

    private getMatchingPanelTypesForModel(conf: DoorActiveConfiguration, passive = false) {
        if (
            (conf.Model && conf.Model.type === 'door_panels' && !passive)
            || (conf.ModelOptions && conf.ModelOptions.passiveSashModel && conf.ModelOptions.passiveSashModel.type === 'door_panels' && passive)
        ) {
            const panelTypes = {
                Double: 'double',
                Outer: 'single',
                Inner: 'single',
                Mixed: 'mixed',
            };
            return Object.keys(panelTypes).reduce((prev, type) => {
                if (
                    (conf.Model && conf.Model.is_double_sided && conf.Model.is_double_sided.includes(panelTypes[type]) && !passive)
                    || (conf.ModelOptions && conf.ModelOptions.passiveSashModel && conf.ModelOptions.passiveSashModel.is_double_sided && conf.ModelOptions.passiveSashModel.is_double_sided.includes(panelTypes[type]) && passive)
                ) {
                    prev.push(type);
                }
                return prev;
            }, []);
        } else {
            const panelTypes = {
                Inset: 'panel_type_inset',
                Outer: 'panel_type_outer',
                Double: 'panel_type_double',
                Inner: 'panel_type_inner',
            };
            return Object.keys(panelTypes).reduce((prev, type) => {
                if (conf.Model && conf.Model.panel_type === panelTypes[type]) {
                    prev.push(type);
                }
                return prev;
            }, []);
        }
    }

    private getCommonFillingsInSash(
        matchingFillings,
        field,
        sash,
        conf: WindowActiveConfiguration,
        noValidation = false
    ) {
        if (matchingFillings.length === 0) {
            matchingFillings = this.getMatchingFillingsInSash(field, sash, conf, noValidation);
        } else {
            matchingFillings = this.getCommonFillings(
                matchingFillings,
                this.getMatchingFillingsInSash(field, sash, conf, noValidation)
            );
        }
        return matchingFillings;
    }

    private getCommonFillings(list1: any[], list2: any[]) {
        return list1.filter(function(a) {
            return this.has(a.id);
        }, list2.reduce((hash, b) => hash.add(b.id), new Set()));
    }

    private getCommonPanelGlazingsInSash(matchingFillings, field) {
        if (matchingFillings.length === 0) {
            matchingFillings = this.getMatchingPanelGlazingsForSash(field);
        } else {
            matchingFillings = this.getCommonFillings(
                matchingFillings,
                this.getMatchingPanelGlazingsForSash(field)
            );
        }
        return matchingFillings;
    }

    private getCommonPanelTrimsInSash(matchingTrims: IccDoorPanelTrim[], field: ActiveSash, face: 'outside' | 'inside' = 'outside') {
        if (matchingTrims.length === 0) {
            matchingTrims = this.getMatchingPanelTrimsForSash(field, this.configurationsService.conf.Current, face);
        } else {
            matchingTrims = this.getCommonFillings(
                matchingTrims,
                this.getMatchingPanelTrimsForSash(field, this.configurationsService.conf.Current, face)
            );
        }
        return matchingTrims;
    }


    private getCommonInnerPanelsInSash(
        matchingFillings,
        field,
        sash,
        conf: DoorActiveConfiguration
    ) {
        if (matchingFillings.length === 0) {
            matchingFillings = this.getMatchingInnerPanelsForSash(field, sash, conf);
        } else {
            matchingFillings = this.getCommonFillings(
                matchingFillings,
                this.getMatchingInnerPanelsForSash(field, sash, conf)
            );
        }
        return matchingFillings;
    }

    private isValidPanelType(sash) {
        return this.getMatchingPanelTypesForSash(sash).indexOf(sash.panelType) > -1;
    }

    private isValidPanelGlazing(sash) {
        const matchingPanelGlazings = this.getMatchingPanelGlazingsForSash(sash)
        .map(f => f.id?.toString());

        return (
            Boolean(sash.panelGlazing
            && matchingPanelGlazings.indexOf(sash.panelGlazing?.id?.toString()) > -1)
            || !matchingPanelGlazings.length && !sash.panelGlazing?.id
        );
    }

    private isValidPanelTrim(sash: ActiveSash, face: 'outside' | 'inside' = 'outside') {
        const matchingPanelTrims = this.getMatchingPanelTrimsForSash(sash, this.configurationsService.conf.Current, face)
            .map(f => f.id?.toString());

        const panelTrim = face === 'inside' ? sash.innerPanelTrim : sash.panelTrim;
        return (face !== 'inside' || face === 'inside' && panelTrim?.id && !sash.innerPanelTrim?.isDefault) &&
        (
            Boolean(panelTrim?.id
                && matchingPanelTrims.indexOf(panelTrim?.id?.toString()) > -1)
            || !matchingPanelTrims.length && !panelTrim?.id
        );
    }

    private isValidPanelTypeForModel(conf: DoorActiveConfiguration) {
        return (
            conf.ModelOptions
            && this.getMatchingPanelTypesForModel(conf).indexOf(conf.ModelOptions.panelType) > -1
        );
    }

    private isValidPanelGlazingForModel(conf: DoorActiveConfiguration) {
        return (
            conf.ModelOptions.panelGlazing
            && this.getMatchingPanelGlazingsForModel(conf)
                .map(f => String(f.id))
                .indexOf(String(conf.ModelOptions.panelGlazing.id)) > -1
        );
    }

    private isValidPanelTrimForModel(conf: DoorActiveConfiguration, face: 'outside' | 'inside' = 'outside') {
        return (
            conf.ModelOptions.panelTrim
            && this.getMatchingPanelTrimsForModel(conf, face)
                .map(f => String(f.id))
                .indexOf(String(face === 'inside' ? conf.ModelOptions.innerPanelTrim?.id : conf.ModelOptions.panelTrim.id)) > -1
        );
    }

    private isValidInnerPanel(sash: ActiveSash, conf: DoorActiveConfiguration, doorSide: 'doorActive' | 'doorActiveInner' | 'doorPassiveInner' | 'doorPassive') {
        let isBaseSideViable = true;
        if (conf.System?.door_type && doorSide === 'doorActive') {
            const panels = this.getMatchingInnerPanelsForSash(sash, sash, conf);
            if (!panels) return;

            const outerPanel = panels.find(p => Number(p.id) === Number(sash.glazing?.id));
            const innerPanel = panels.find(p => Number(p.id) === Number(sash.panelInner?.id));

            if (outerPanel?.base_side === "any" && innerPanel?.base_side === "outside") {
                isBaseSideViable = false;
            }
            if (outerPanel?.base_side === "inside" && innerPanel?.base_side !== "inside") {
                isBaseSideViable = false;
            }
            if (outerPanel?.base_side !== "inside" && innerPanel?.base_side === "inside") {
                isBaseSideViable = false;
            }
            if (outerPanel?.base_side === "outside" && innerPanel?.base_side === "outside" && outerPanel?.id !== innerPanel?.id) {
                isBaseSideViable = false;
            }
        }

        if (conf.System?.door_type && doorSide === 'doorPassive') {
            const passivePanels = this.getMatchingPassiveInnerPanelsForModel(conf, true);
            if (!passivePanels) return;

            const outerPassivePanel = passivePanels.find(p => Number(p.id) === Number(sash.glazing?.id));
            const innerPassivePanel = passivePanels.find(p => Number(p.id) === Number(sash.panelInner?.id));

            if (outerPassivePanel?.base_side === "any" && innerPassivePanel?.base_side === "outside") {
                isBaseSideViable = false;
            }
            if (outerPassivePanel?.base_side === "inside" && innerPassivePanel?.base_side !== "inside") {
                isBaseSideViable = false;
            }
            if (outerPassivePanel?.base_side !== "inside" && innerPassivePanel?.base_side === "inside") {
                isBaseSideViable = false;
            }
            if (outerPassivePanel?.base_side === "outside" && innerPassivePanel?.base_side === "outside" && outerPassivePanel?.id !== innerPassivePanel?.id) {
                isBaseSideViable = false;
            }
        }

        return (
            isBaseSideViable && (
            ((!sash.glazing || sash.glazing.type !== 'door_panels') && !sash.panelInner)
            || (sash.glazing.type === 'door_panels'
                && sash.panelInner
                && this.getMatchingInnerPanelsForSash(sash, sash, conf)
                    .map(f => Number(f.id))
                    .indexOf(Number(sash.panelInner.id)) > -1))
        );
    }

    private isValidInnerPanelForModel(conf: DoorActiveConfiguration) {
        return (
            conf.ModelOptions.panelInner
            && this.getMatchingInnerPanelsForModel(conf)
                .map(f => Number(f.id))
                .indexOf(Number(conf.ModelOptions.panelInner.id)) > -1
        );
    }

    private isValidPassiveInnerPanelForModel(conf: DoorActiveConfiguration) {
        return (
            conf.ModelOptions.passiveInnerSashModel
            && this.getMatchingPassiveInnerPanelsForModel(conf)
                .map(f => Number(f.id))
                .indexOf(Number(conf.ModelOptions.passiveInnerSashModel.id)) > -1
        );
    }

    private getFillingFromId(id: string | null) {
        return this.allFillings.find(filling => filling.id === id) || null;
    }

    private isDoorPanel(filling: IccFilling) {
        return filling.type === 'deco_panels' || filling.type === 'door_panels';
    }

    isDoorPassiveSash(field: ActiveSash) {
        return field.type && (field.type.type === 'DRP' || field.type.type === 'DOP');
    }

    getUniqueOrderedFillings(fillings) {
        let distinctFillings = [];
        fillings.forEach(item => {
            if (!distinctFillings.includes(item.id)){
                distinctFillings.push(item.id);
            }
        });
        fillings = fillings.filter(filling=>{
            if (distinctFillings.includes(filling.id)){
                distinctFillings = distinctFillings.filter(el=>el!==filling.id);
                return true;
            } else {
                return false;
            }
        })
        return fillings;
    }

    getSashWidthInRebateBasedOnWidth(conf: DoorActiveConfiguration | WindowActiveConfiguration) {
        const frameProfile = conf.UsedProfiles.find(p => p.type === 'frame') || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const leftLightSize = (conf.doorSizes && conf.doorSizes.leftLightSize) || 0;
        const rightLightSize = (conf.doorSizes && conf.doorSizes.rightLightSize) || 0;

        return Number(conf.Width) - Number(leftLightSize) - Number(rightLightSize) - Number(2 * frameProfile?.spaceSash || 0) + (this.getBiggerRebateValueFromVirtualDoorSash(conf) * 2);
    }

    getSashHeightInRebateBasedOnHeight(conf: DoorActiveConfiguration | WindowActiveConfiguration) {
        const frame = conf.UsedProfiles.find(p => p.type === 'frame') || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const threshold = conf.UsedProfiles.find(p => p.type === 'threshold') || this.profilesService.getProfile(conf.ProfileSet.threshold);
        const topLight = (conf.doorSizes && conf.doorSizes.topLightSize) || 0;

        return Number(conf.Height) - Number(topLight) - Number(frame?.spaceSash) + Number(threshold?.spaceSash) + this.getBiggerRebateValueFromVirtualDoorSash(conf);
    }

    getBiggerRebateValueFromVirtualDoorSash(conf: DoorActiveConfiguration | WindowActiveConfiguration) {
        const virtualDoorSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');

        if (virtualDoorSash) {
            if (virtualDoorSash.rebateWidth && virtualDoorSash.rebateWidthOut) {
                return Math.max(virtualDoorSash.rebateWidth, virtualDoorSash.rebateWidthOut)
            } else if (virtualDoorSash.rebateWidth && !virtualDoorSash.rebateWidthOut) {
                return virtualDoorSash.rebateWidth;
            } else if (!virtualDoorSash.rebateWidth && virtualDoorSash.rebateWidthOut) {
                return virtualDoorSash.rebateWidthOut
            } else {
                return 0;
            }
        }
    }

    getSashWidthBasedOnWidth(conf: DoorActiveConfiguration | WindowActiveConfiguration, withRebate = true) {
        const frameProfile = conf.UsedProfiles.find(p => p.type === 'frame') || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const leftLightSize = (conf.doorSizes && conf.doorSizes.leftLightSize) || 0;
        const rightLightSize = (conf.doorSizes && conf.doorSizes.rightLightSize) || 0;

        return Number(conf.Width) - Number(leftLightSize) - Number(rightLightSize) - Number(2 * frameProfile?.spaceSash || 0) + (this.getRebateValueFromVirtualDoorSash(conf, withRebate) * 2);
    }

    getSashHeightBasedOnHeight(conf: DoorActiveConfiguration | WindowActiveConfiguration, withRebate = true) {
        const frame = conf.UsedProfiles.find(p => p.type === 'frame') || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const threshold = conf.UsedProfiles.find(p => p.type === 'threshold') || this.profilesService.getProfile(conf.ProfileSet.threshold);
        const topLight = (conf.doorSizes && conf.doorSizes.topLightSize) || 0;
        const rebate = conf.System.available_threshold_sash_rebate ? 1 : 2;

        return Number(conf.Height) - Number(topLight) - Number(frame?.spaceSash) - Number(threshold?.spaceSash || 0) + (this.getRebateValueFromVirtualDoorSash(conf, withRebate) * rebate);
    }

    getRebateValueFromVirtualDoorSash(conf: DoorActiveConfiguration | WindowActiveConfiguration, max = true) {
        const virtualDoorSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');
        if (virtualDoorSash) {
            if (virtualDoorSash.rebateWidth && virtualDoorSash.rebateWidthOut) {
                return max
                    ? Math.max(virtualDoorSash.rebateWidth, virtualDoorSash.rebateWidthOut)
                    : Math.min(virtualDoorSash.rebateWidth, virtualDoorSash.rebateWidthOut)
            } else if (virtualDoorSash.rebateWidth && !virtualDoorSash.rebateWidthOut) {
                return virtualDoorSash.rebateWidth;
            } else if (!virtualDoorSash.rebateWidth && virtualDoorSash.rebateWidthOut) {
                return virtualDoorSash.rebateWidthOut
            } else {
                return 0;
            }
        }
    }
}
